import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  Star, RefreshCw, Download, Search, ExternalLink, Loader2, Trash2, MessageSquare,
  CheckCircle2, Clock, AlertCircle, AlertTriangle, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status presentation (icons + colors). Keep keys in sync with the backend.
const STATUS_META = {
  recenzie_primita: {
    label: "Recenzie Primită",
    Icon: Clock,
    chip: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    dot: "bg-amber-400",
  },
  platita: {
    label: "Plătită",
    Icon: CheckCircle2,
    chip: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    dot: "bg-emerald-400",
  },
};

const STATUS_ORDER = ["recenzie_primita", "platita"];

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.recenzie_primita;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.chip}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function FamilySettingsRow({ g, onSaved }) {
  // Local optimistic state to avoid flicker while reload is in-flight.
  const [target, setTarget] = useState(String(g.target_next_reviews || ""));
  const [lastOrder, setLastOrder] = useState(g.last_order_added || "");
  const [saving, setSaving] = useState(false);

  const rate = Number(g.real_conversion_rate || 0);
  const nTarget = Number(target) || 0;
  const contactsNeeded = rate > 0 && nTarget > 0 ? Math.ceil(nTarget / rate) : null;
  const units30 = Number(g.units_sold_last_30_days || 0);
  const estCostPerReview = nTarget > 0 && units30 > 0 ? (nTarget * 50) / units30 : null;

  const save = async (patch) => {
    setSaving(true);
    try {
      await axios.put(`${API}/reviews/product-groups/${g.id}/settings`, patch);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Salvare eșuată: " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/50 p-2 space-y-1.5">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[9px] uppercase text-zinc-500">Următoarele recenzii (țintă)</label>
          <input
            data-testid={`family-target-${g.id}`}
            type="number" min="0" value={target}
            onChange={(e) => setTarget(e.target.value)}
            onBlur={() => Number(target) !== Number(g.target_next_reviews || 0) && save({ target_next_reviews: Number(target) || 0 })}
            className="mt-0.5 w-full rounded border border-zinc-800 bg-zinc-900 px-1.5 py-1 text-xs text-zinc-100 focus:border-orange-500/50"
          />
        </div>
        <div>
          <label className="text-[9px] uppercase text-zinc-500">Contacte de adăugat</label>
          <div className="mt-0.5 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-1 text-xs">
            {contactsNeeded != null ? (
              <span className="font-mono font-bold text-orange-300">{contactsNeeded}</span>
            ) : rate <= 0 ? (
              <span className="text-[10px] text-zinc-600">rată reală 0</span>
            ) : (
              <span className="text-zinc-600">—</span>
            )}
            {contactsNeeded != null && (
              <span className="ml-1 text-[9px] text-zinc-500">
                @ {(rate * 100).toFixed(1)}%
              </span>
            )}
          </div>
        </div>
        <div>
          <label
            className="text-[9px] uppercase text-zinc-500"
            title="(țintă × 50 lei) / bucăți vândute 30z (FBE RO finalizate)"
          >
            Cost/rec estimat
          </label>
          <div
            data-testid={`family-est-cost-per-review-${g.id}`}
            className="mt-0.5 rounded border border-zinc-800 bg-zinc-900 px-1.5 py-1 text-xs"
            title="(țintă × 50 lei) / bucăți vândute 30z (FBE RO finalizate)"
          >
            {estCostPerReview != null ? (
              <span className="font-mono font-bold text-orange-300">
                {estCostPerReview.toFixed(2)} lei
              </span>
            ) : nTarget <= 0 ? (
              <span className="text-[10px] text-zinc-600">setează țintă</span>
            ) : units30 <= 0 ? (
              <span className="text-[10px] text-zinc-600">fără vânzări 30z</span>
            ) : (
              <span className="text-zinc-600">—</span>
            )}
          </div>
        </div>
      </div>
      <div>
        <label className="text-[9px] uppercase text-zinc-500">Ultima comandă adăugată</label>
        <input
          data-testid={`family-last-order-${g.id}`}
          type="date" value={lastOrder}
          onChange={(e) => setLastOrder(e.target.value)}
          onBlur={() => (lastOrder || "") !== (g.last_order_added || "") && save({ last_order_added: lastOrder })}
          disabled={saving}
          className="mt-0.5 w-full rounded border border-zinc-800 bg-zinc-900 px-1.5 py-1 text-xs text-zinc-100 focus:border-orange-500/50"
        />
      </div>
    </div>
  );
}

function FamilyOrdersLink({ g }) {
  // Filter window: date_from = ultima_comanda + 1 day (excludes the day),
  //                date_to   = today - 30 days (older than 30 days).
  // User request 2026-07-31: pentru identificarea următoarelor comenzi ce ar
  // trebui contactate pentru recenzii, cu SKU-urile din familie + eMAG RO
  // + BG FBE + status finalizat (BG adăugat 2026-08-18).
  const [busy, setBusy] = useState(false);
  if (!g.last_order_added) {
    return (
      <div className="text-[10px] text-zinc-600 italic">
        Completează „Ultima comandă adăugată” ca să obții link către comenzi.
      </div>
    );
  }
  const today = new Date();
  const dateTo = new Date(today);
  dateTo.setDate(dateTo.getDate() - 30);
  const lastOrderDate = new Date(g.last_order_added);
  const dateFrom = new Date(lastOrderDate);
  dateFrom.setDate(dateFrom.getDate() + 1); // exclude the last-order day
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (dateFrom > dateTo) {
    return (
      <div className="text-[10px] text-rose-400 italic">
        Fereastra e goală ({fmt(dateFrom)} → {fmt(dateTo)}). Ajustează data.
      </div>
    );
  }
  const params = new URLSearchParams({
    // User request 2026-08-18: include BG FBE too so we surface Bulgarian
    // orders alongside RO for the same family.
    platforms: "emag_ro_fbe",
    statuses: "4",
    date_from: fmt(dateFrom),
    date_to: fmt(dateTo),
    sku: (g.skus || []).join(","),
    // Excel columns matching what operators pick manually.
    fields: "customer_name,phone,order_id,product_link,sku,order_date",
  });
  // User request 2026-08-18: click should download the .xlsx directly
  // (previously it navigated to /orders where the operator had to select
  // and export manually). Uses axios so the Authorization header is sent.
  const download = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API}/orders/export?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Comenzi_recenzii_${g.name || g.id}_${fmt(dateFrom)}_${fmt(dateTo)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descărcat.");
    } catch (e) {
      toast.error(`Descărcarea a eșuat: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={download}
      disabled={busy}
      data-testid={`family-orders-link-${g.id}`}
      className="inline-flex w-full items-center justify-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[11px] font-medium text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
    >
      {busy ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
      {busy ? "Descarc..." : `Excel comenzi FBE RO+BG (${fmt(dateFrom)} → ${fmt(dateTo)})`}
    </button>
  );
}

// Small helper — link to the eMAG product page for a family. All PNKs in a
// family land on the same product listing, so we open the first PNK by
// default. Returns null if no PNK is available (family not yet resolved).
function FamilyEmagLink({ pnks, testid, label }) {
  const list = Array.isArray(pnks) ? pnks.filter(Boolean) : [];
  if (list.length === 0) return null;
  const primaryPnk = list[0];
  const href = `https://www.emag.ro/-/pd/${primaryPnk}`;
  const title = list.length === 1
    ? `Deschide produsul pe eMAG (PNK ${primaryPnk})`
    : `Deschide unul din cele ${list.length} produse pe eMAG (PNK ${primaryPnk})`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid || "family-emag-link"}
      title={title}
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-1 rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-200 hover:bg-orange-500/20"
    >
      <ExternalLink className="h-3 w-3" />
      {label || "eMAG"}
    </a>
  );
}



function ProductGroupsTab() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // group being edited
  const [drilldown, setDrilldown] = useState(null); // group being drilled into

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews/product-groups`);
      setGroups(r.data.items || []);
    } catch (e) {
      toast.error("Nu am putut încărca grupurile", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteGroup = async (g) => {
    if (!window.confirm(`Ștergi familia "${g.name}"?`)) return;
    try {
      await axios.delete(`${API}/reviews/product-groups/${g.id}`);
      toast.success(`Familia "${g.name}" ștearsă.`);
      load();
    } catch (e) {
      toast.error("Ștergere eșuată", { description: e.response?.data?.detail || e.message });
    }
  };

  const totalReviews = groups.reduce((s, g) => s + (g.review_count || 0), 0);

  // User request 2026-08-07: on „Recenzii pe produs" show ONLY families that
  // have been extracted in „Recenzii și Întrebări eMAG". The backend sets
  // `emag_total_reviews` only when at least one PNK stats row exists — so
  // that field being null means „extract-ul nu a rulat încă pe familia asta".
  const visibleGroups = groups.filter((g) => g.emag_total_reviews != null);
  const hiddenCount = groups.length - visibleGroups.length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Familii de produse</h2>
          <p className="text-xs text-zinc-500">
            Fiecare familie grupează unul sau mai multe SKU-uri (ex: Mouse alb + Mouse negru) și afișează câte recenzii au primit împreună.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500">
            {visibleGroups.length} familii · <span className="font-mono text-amber-300">{totalReviews}</span> recenzii totale
            {hiddenCount > 0 && (
              <span className="ml-1 text-[10px] text-zinc-600">(+{hiddenCount} fără extract eMAG)</span>
            )}
          </span>
          <button
            data-testid="add-product-group-btn"
            onClick={() => { setEditing(null); setAddOpen(true); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 text-xs font-semibold text-orange-200 hover:bg-orange-500/20">
            <Star className="h-3.5 w-3.5" /> Adaugă familie
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-zinc-500">Se încarcă...</p>}

      {!loading && groups.length === 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-8 text-center">
          <Star className="mx-auto h-8 w-8 text-zinc-600" />
          <p className="mt-2 text-sm text-zinc-400">Nicio familie încă.</p>
          <p className="mt-1 text-xs text-zinc-500">Apasă &bdquo;Adaugă familie&rdquo; ca să grupezi SKU-uri și să vezi câte recenzii au primit împreună.</p>
        </div>
      )}

      {!loading && groups.length > 0 && visibleGroups.length === 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
          <p className="mt-2 text-sm text-amber-200">Nicio familie nu are recenzii extrase din eMAG.</p>
          <p className="mt-1 text-xs text-zinc-500">Deschide tabul &bdquo;Recenzii și Întrebări eMAG&rdquo; și apasă &bdquo;Extrage recenzii&rdquo; pe fiecare familie pentru care vrei să vezi statisticile aici.</p>
        </div>
      )}

      {!loading && visibleGroups.length > 0 && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleGroups.map((g) => (
            <div key={g.id} data-testid={`product-group-${g.id}`}
              className="group rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 transition hover:border-orange-500/40 cursor-pointer"
              onClick={() => setDrilldown(g)}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-zinc-100">{g.name}</h3>
                <div className="flex items-center gap-1.5">
                  <FamilyEmagLink pnks={g.pnks} testid={`product-group-${g.id}-emag-link`} />
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-200">
                    <Star className="h-3 w-3" />
                    {g.review_count || 0}
                  </span>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {(g.skus || []).map((s) => (
                  <span key={s} className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-mono text-zinc-300">
                    {s}
                  </span>
                ))}
              </div>
              {(g.da_count > 0 || g.contacted_total > 0 || g.contacts_to_call > 0) && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-2 py-1.5"
                    title={`${g.review_from_sheet || 0} recenzii / ${g.da_count || 0} DA`}>
                    <div className="text-[9px] uppercase tracking-wider text-sky-300/80">Rată conversie</div>
                    <div className="font-mono text-sm font-bold text-sky-300">
                      {(Number(g.conversion_rate || 0) * 100).toFixed(1)}%
                    </div>
                    <div className="text-[9px] text-zinc-500">{g.review_from_sheet || 0} / {g.da_count || 0}</div>
                  </div>
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5"
                    title={`Rată REALĂ: ${g.review_from_sheet || 0} recenzii / ${g.contacted_total || 0} contactați (DA+NU)`}>
                    <div className="text-[9px] uppercase tracking-wider text-amber-300/80">Rată reală</div>
                    <div className="font-mono text-sm font-bold text-amber-300">
                      {(Number(g.real_conversion_rate || 0) * 100).toFixed(1)}%
                    </div>
                    <div className="text-[9px] text-zinc-500">{g.review_from_sheet || 0} / {g.contacted_total || 0}</div>
                  </div>
                  <div className="rounded-md border border-violet-500/30 bg-violet-500/5 px-2 py-1.5"
                    data-testid={`family-contacts-to-call-${g.id || g._id}`}
                    title="Rânduri din Google Sheet unde col M (Data Apel) e goală — clienți de sunat pentru această familie">
                    <div className="text-[9px] uppercase tracking-wider text-violet-300/80">De sunat</div>
                    <div className="font-mono text-sm font-bold text-violet-200">
                      {g.contacts_to_call || 0}
                    </div>
                    <div className="text-[9px] text-zinc-500">rânduri fără dată apel</div>
                  </div>
                </div>
              )}
              {g.missing_skus && g.missing_skus.length > 0 && (
                <p className="mt-2 text-[10px] text-amber-500/80">
                  ⚠ SKU-uri neregăsite: {g.missing_skus.join(", ")}
                </p>
              )}

              {/* NEW stats block — user request 2026-07-31.
                  Click swallowed so drill-down modal doesn't open on interactions. */}
              <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                <div className="grid grid-cols-4 gap-1.5">
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-1.5 py-1.5" title="Recenzii importate în ultimele 14 zile (fereastră glisantă)">
                    <div className="text-[9px] uppercase text-emerald-300/80">Recenzii 14z</div>
                    <div className="font-mono text-sm font-bold text-emerald-300">{g.reviews_last_14_days || 0}</div>
                  </div>
                  <div className="rounded-md border border-teal-500/30 bg-teal-500/5 px-1.5 py-1.5" title="Recenzii importate în ultimele 30 zile (fereastră glisantă)">
                    <div className="text-[9px] uppercase text-teal-300/80">Recenzii 30z</div>
                    <div className="font-mono text-sm font-bold text-teal-300">{g.reviews_last_30_days || 0}</div>
                  </div>
                  <div className="rounded-md border border-zinc-800 bg-zinc-950/70 px-1.5 py-1.5" title="Bucăți vândute în ultimele 30 zile (FBE RO+BG, comenzi finalizate)">
                    <div className="text-[9px] uppercase text-zinc-400">Buc. 30z</div>
                    <div className="font-mono text-sm font-bold text-zinc-100">{g.units_sold_last_30_days || 0}</div>
                  </div>
                  <div className="rounded-md border border-orange-500/30 bg-orange-500/5 px-1.5 py-1.5" title="(recenzii 30z × 50 lei) / bucăți vândute 30z">
                    <div className="text-[9px] uppercase text-orange-300/80">Cost/rec</div>
                    <div className="font-mono text-sm font-bold text-orange-300">
                      {g.cost_per_review != null ? `${g.cost_per_review} lei` : "—"}
                    </div>
                  </div>
                </div>

                {/* Profit/produs după recenzii — user request 2026-08-18.
                    (avg profit / unit ultimele 30z FBE RO+BG) − cost/rec
                    Cost/rec e deja per-unit, deci se scade direct. */}
                {g.avg_profit_per_unit_last_30_days != null && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-1.5 py-1.5" title="Profit mediu per bucată (total profit 30z / buc. 30z) — RON, incl. BG convertit BNR">
                      <div className="text-[9px] uppercase text-sky-300/80">Profit mediu/buc 30z</div>
                      <div className="font-mono text-sm font-bold text-sky-300">
                        {g.avg_profit_per_unit_last_30_days} lei
                        {g.avg_profit_pct != null && (
                          <span className="ml-1 text-[10px] font-normal text-sky-300/70">({g.avg_profit_pct}%)</span>
                        )}
                      </div>
                    </div>
                    <div
                      className="rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 px-1.5 py-1.5"
                      title="Profit/buc după ce am plătit costul recenziilor: profit mediu − cost/rec"
                    >
                      <div className="text-[9px] uppercase text-fuchsia-300/80">Profit după recenzii</div>
                      <div className="font-mono text-sm font-bold text-fuchsia-300">
                        {g.profit_after_reviews_per_unit != null ? `${g.profit_after_reviews_per_unit} lei` : "—"}
                        {g.profit_after_reviews_pct != null && (
                          <span className="ml-1 text-[10px] font-normal text-fuchsia-300/70">({g.profit_after_reviews_pct}%)</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Editable settings */}
                <FamilySettingsRow g={g} onSaved={load} />

                {/* Link to Orders filtered to this family */}
                <FamilyOrdersLink g={g} />

                {/* eMAG rating distribution (populated by eMAG reviews scraper) */}
                {g.emag_total_reviews != null && (
                  <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase text-amber-300/80">Recenzii eMAG (total)</span>
                      <span className="font-mono text-sm font-bold text-amber-200">{g.emag_total_reviews}</span>
                    </div>
                    <div className="mt-1 grid grid-cols-5 gap-1">
                      {[5,4,3,2,1].map((s) => {
                        const n = Number((g.emag_rating_distribution || {})[String(s)] || 0);
                        const isPos = s >= 4;
                        return (
                          <div key={s}
                            data-testid={`family-${g.id}-star-${s}`}
                            title={`${s} stele: ${n} recenzii`}
                            className={`rounded border px-1 py-0.5 text-center ${isPos ? "border-emerald-500/30 bg-emerald-500/5" : "border-red-500/30 bg-red-500/5"}`}>
                            <div className={`text-[9px] ${isPos ? "text-emerald-300/80" : "text-red-300/80"}`}>{s}★</div>
                            <div className={`font-mono text-[11px] font-bold ${isPos ? "text-emerald-200" : "text-red-200"}`}>{n}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex gap-2 opacity-0 transition group-hover:opacity-100">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditing(g); setAddOpen(true); }}
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] text-zinc-300 hover:border-sky-500/40 hover:text-sky-200">
                  Editează
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteGroup(g); }}
                  className="rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] text-zinc-300 hover:border-red-500/40 hover:text-red-200">
                  Șterge
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {addOpen && (
        <ProductGroupModal
          initial={editing}
          onClose={() => { setAddOpen(false); setEditing(null); }}
          onSaved={() => { setAddOpen(false); setEditing(null); load(); }}
        />
      )}

      {drilldown && (
        <ProductGroupDrilldown
          group={drilldown}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}

function ProductGroupModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [skusText, setSkusText] = useState((initial?.skus || []).join(", "));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    const skus = skusText.split(/[,;\s\n]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
    if (!name.trim()) { setError("Numele familiei e obligatoriu."); return; }
    if (skus.length === 0) { setError("Adaugă cel puțin un SKU."); return; }
    setBusy(true);
    try {
      if (initial) {
        await axios.patch(`${API}/reviews/product-groups/${initial.id}`, { name: name.trim(), skus });
        toast.success(`Familia "${name}" actualizată.`);
      } else {
        await axios.post(`${API}/reviews/product-groups`, { name: name.trim(), skus });
        toast.success(`Familia "${name}" creată.`);
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()}
        data-testid="product-group-modal">
        <h3 className="text-lg font-semibold text-zinc-100">
          {initial ? "Editează familia" : "Adaugă familie de produse"}
        </h3>
        <p className="mt-1 text-xs text-zinc-500">
          Introdu numele familiei și lista de SKU-uri (separate prin virgulă, spațiu sau linie nouă).
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">Nume familie</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="group-name-input"
              placeholder="ex: Mouse"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/50" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">SKU-uri</label>
            <textarea
              value={skusText}
              onChange={(e) => setSkusText(e.target.value)}
              data-testid="group-skus-input"
              rows={3}
              placeholder="BRAG-140, BRAG-149"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-mono text-zinc-100 outline-none focus:border-orange-500/50" />
          </div>
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">
            Renunță
          </button>
          <button onClick={save} disabled={busy}
            data-testid="group-save-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/50 bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-50">
            {busy ? "Se salvează..." : (initial ? "Salvează" : "Creează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductGroupDrilldown({ group, onClose }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reviews/product-groups/${group.id}/reviews?page=1&page_size=100`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error("Eroare la încărcare", { description: e.message }))
      .finally(() => setLoading(false));
  }, [group.id]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950 p-6"
        data-testid="product-group-drilldown"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold text-zinc-100">{group.name}</h3>
            <p className="mt-1 text-xs text-zinc-500">
              {(group.skus || []).map((s) => (
                <span key={s} className="mr-1 rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-zinc-300">{s}</span>
              ))}
            </p>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-sm font-bold text-amber-200">
            <Star className="h-3 w-3" /> {total}
          </span>
        </div>
        {loading && <p className="mt-4 text-sm text-zinc-500">Se încarcă recenziile...</p>}
        {!loading && items.length === 0 && (
          <p className="mt-4 text-sm text-zinc-500">Nicio recenzie încă pentru această familie.</p>
        )}
        {!loading && items.length > 0 && (
          <table className="mt-4 w-full text-sm">
            <thead className="text-xs uppercase text-zinc-500">
              <tr>
                <th className="pb-2 text-left">Client</th>
                <th className="pb-2 text-left">Telefon</th>
                <th className="pb-2 text-left">Data apel</th>
                <th className="pb-2 text-left">Status</th>
                <th className="pb-2 text-left">Link</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-zinc-800/60">
                  <td className="py-2 text-zinc-200">{r.nume || "—"}</td>
                  <td className="py-2 font-mono text-xs text-zinc-400">{r.telefon || "—"}</td>
                  <td className="py-2 text-xs text-zinc-400">{r.data_apel || "—"}</td>
                  <td className="py-2"><StatusChip status={r.status} /></td>
                  <td className="py-2">
                    {r.link_dovada && (
                      <a href={r.link_dovada} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300">
                        <ExternalLink className="h-3 w-3" /> Dovadă
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300">
            Închide
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Reviews() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState(""); // "" = toate
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState(""); // debounced
  const [statusCounts, setStatusCounts] = useState({});
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const [vouchers, setVouchers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Honour the ?tab=<name> query — used when we open the Reviews page
    // in a new tab from a Gomag reply job (the parent tab passes
    // ?tab=gomag&job=<id> so the child tab lands on the right sub-view).
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      if (t && ["reviews", "groups", "emag", "gomag", "customer"].includes(t)) return t;
    } catch { /* SSR / privacy mode — fall through */ }
    return "reviews";
  });
  const [markCount, setMarkCount] = useState("");
  const [marking, setMarking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews`, {
        params: {
          status: statusFilter || undefined,
          search: searchTerm || undefined,
          page,
          page_size: pageSize,
        },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setStatusCounts(r.data.status_counts || {});
      setLastSyncAt(r.data.last_sync_at || null);
      setLastSummary(r.data.last_sync_summary || null);
    } catch (e) {
      toast.error("Nu am putut încărca recenziile", { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  // Vouchers state — refreshed via same 30-min background loop (`vouchers_sync`)
  const loadVouchers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reviews/vouchers`);
      setVouchers(r.data);
    } catch { /* silent — badge just won't show */ }
  }, []);
  useEffect(() => {
    loadVouchers();
    const iv = setInterval(loadVouchers, 60_000);  // frontend re-poll every 1 min
    return () => clearInterval(iv);
  }, [loadVouchers]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/reviews/sync`);
      const s = r.data || {};
      toast.success(
        `Sincronizare finalizată`,
        { description: `Noi: ${s.imported_new ?? 0} · Actualizate: ${s.updated ?? 0} · Ignorate (fără link): ${s.skipped_empty_link ?? 0}` },
      );
      await load();
    } catch (e) {
      toast.error("Sincronizare eșuată", { description: e.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  const onChangeStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/reviews/${id}/status`, { status: newStatus });
      // Optimistic update
      setItems((prev) => prev.map((r) => r.id === id ? { ...r, status: newStatus } : r));
      // Recount
      setStatusCounts((prev) => {
        const next = { ...prev };
        const old = items.find((x) => x.id === id)?.status || "recenzie_primita";
        if (old !== newStatus) {
          next[old] = Math.max(0, (next[old] || 0) - 1);
          next[newStatus] = (next[newStatus] || 0) + 1;
        }
        return next;
      });
    } catch (e) {
      toast.error("Nu am putut schimba statusul", { description: e.response?.data?.detail || e.message });
    }
  };

  const onBulkStatus = async (newStatus) => {
    if (!selectedIds.size) return;
    const ids = Array.from(selectedIds);
    const label = STATUS_META[newStatus].label;
    if (!window.confirm(`Schimbi statusul a ${ids.length} recenzii în "${label}"?`)) return;
    setBulkApplying(true);
    try {
      const r = await axios.post(`${API}/reviews/bulk-status`, { ids, status: newStatus });
      toast.success(`${r.data.modified || 0} recenzii actualizate`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error("Bulk update eșuat", { description: e.response?.data?.detail || e.message });
    } finally {
      setBulkApplying(false);
    }
  };

  const onExport = async () => {
    try {
      const r = await axios.get(`${API}/reviews/export`, {
        params: {
          status: statusFilter || undefined,
          search: searchTerm || undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:-]/g, "");
      a.href = url;
      a.download = `reviews_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  const toggleSelectAll = (checked) => {
    if (checked) setSelectedIds(new Set(items.map((x) => x.id)));
    else setSelectedIds(new Set());
  };
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allChecked = items.length > 0 && items.every((x) => selectedIds.has(x.id));
  const someChecked = items.some((x) => selectedIds.has(x.id));

  const grandTotal = useMemo(
    () => Object.values(statusCounts).reduce((a, b) => a + Number(b || 0), 0),
    [statusCounts],
  );
  // Neplătite = total - platite. Fiecare recenzie neplătită costă 6 EUR
  // (voucher). User request 2026-07-24.
  const unpaidCount = Math.max(0, grandTotal - Number(statusCounts.platita || 0));
  const unpaidAmount = unpaidCount * 6;

  return (
    <div className="space-y-6 p-6" data-testid="reviews-page">
      {/* Header */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-zinc-50">
            <Star className="h-6 w-6 text-amber-400" />
            Recenzii clienți
          </h1>
          <p className="mt-1 text-xs text-zinc-500">
            Sursă: Google Sheet · Sync automat la 30 min · Doar rândurile cu „Link dovada recenzie&rdquo; completat.
          </p>
          {vouchers && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5"
              data-testid="vouchers-badge">
              <span className="text-[10px] uppercase tracking-wider text-emerald-300/80">Vouchere disponibile</span>
              <span className="font-mono text-sm font-bold text-emerald-300">
                {vouchers.available}<span className="text-zinc-500">/{vouchers.total}</span>
              </span>
              {vouchers.used > 0 && (
                <span className="text-[10px] text-zinc-500">
                  ({vouchers.used} folosite)
                </span>
              )}
            </div>
          )}
          {lastSummary && lastSummary.considered > 0 && (
            <div className="mt-3 ml-2 inline-flex items-center gap-2 rounded-lg border border-sky-500/30 bg-sky-500/5 px-3 py-1.5"
              data-testid="conversion-rate-badge"
              title={`${lastSummary.converted} recenzii postate (col R) / ${lastSummary.considered} oameni care au răspuns la telefon (col N = DA)`}>
              <span className="text-[10px] uppercase tracking-wider text-sky-300/80">Rată conversie</span>
              <span className="font-mono text-sm font-bold text-sky-300">
                {(Number(lastSummary.conversion_rate || 0) * 100).toFixed(2)}%
              </span>
              <span className="text-[10px] text-zinc-500">
                ({lastSummary.converted} recenzii / {lastSummary.considered} au răspuns)
              </span>
            </div>
          )}
          {lastSummary && lastSummary.contacted_total > 0 && (
            <div className="mt-3 ml-2 inline-flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-1.5"
              data-testid="real-conversion-rate-badge"
              title={`Rata REALĂ: ${lastSummary.converted} recenzii postate / ${lastSummary.contacted_total} oameni contactați efectiv (DA + NU în col N)`}>
              <span className="text-[10px] uppercase tracking-wider text-amber-300/80">Rată reală conversie</span>
              <span className="font-mono text-sm font-bold text-amber-300">
                {(Number(lastSummary.real_conversion_rate || 0) * 100).toFixed(2)}%
              </span>
              <span className="text-[10px] text-zinc-500">
                ({lastSummary.converted} / {lastSummary.contacted_total} contactați · DA+NU)
              </span>
            </div>
          )}
          {lastSummary && lastSummary.contacts_to_call_total > 0 && (
            <div className="mt-3 ml-2 inline-flex items-center gap-2 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-1.5"
              data-testid="contacts-to-call-badge"
              title="Contacte rămase pentru sunat = rândurile din Google Sheet unde col M (Data Apel) este GOALĂ, cu PNK identificat din col B">
              <span className="text-[10px] uppercase tracking-wider text-violet-300/80">Contacte de sunat</span>
              <span className="font-mono text-sm font-bold text-violet-200">
                {lastSummary.contacts_to_call_total}
              </span>
              <span className="text-[10px] text-zinc-500">
                (rânduri fără dată apel · pe familii ↓)
              </span>
            </div>
          )}
          {unpaidCount > 0 && (
            <div className="mt-3 ml-2 inline-flex flex-wrap items-center gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-1.5"
              data-testid="unpaid-reviews-badge"
              title={`${unpaidCount} recenzii neplătite × 6 EUR/recenzie = ${unpaidAmount} EUR de plătit`}>
              <span className="text-[10px] uppercase tracking-wider text-rose-300/80">Recenzii neplătite</span>
              <span className="font-mono text-sm font-bold text-rose-200">{unpaidCount}</span>
              <span className="text-[10px] text-zinc-500">
                (× 6 EUR = <span className="font-mono font-bold text-rose-300">{unpaidAmount} EUR</span>)
              </span>
              <span className="mx-1 h-4 w-px bg-rose-500/30" />
              <span className="text-[10px] text-rose-300/80">Marchează plătite ultimele</span>
              <input
                data-testid="mark-oldest-count"
                type="number"
                min="1"
                max={unpaidCount}
                value={markCount}
                onChange={(e) => setMarkCount(e.target.value)}
                placeholder="ex: 12"
                className="w-16 rounded border border-rose-500/40 bg-zinc-950 px-2 py-0.5 text-center text-xs font-mono text-rose-100 outline-none focus:border-rose-400"
              />
              <button
                data-testid="mark-oldest-btn"
                disabled={marking || !markCount || Number(markCount) <= 0}
                onClick={async () => {
                  const n = Number(markCount);
                  if (!n || n <= 0) return;
                  if (!window.confirm(`Marchezi ca PLĂTITE cele mai vechi ${n} recenzii neplătite?`)) return;
                  setMarking(true);
                  try {
                    const r = await axios.post(`${API}/reviews/mark-oldest-paid`, { count: n });
                    toast.success(`${r.data.modified} recenzii marcate ca plătite`, {
                      description: r.data.found < n ? `(cerut ${n}, găsite doar ${r.data.found})` : undefined,
                    });
                    setMarkCount("");
                    load();
                  } catch (e) {
                    toast.error("Eroare la marcare", { description: e.response?.data?.detail || e.message });
                  } finally {
                    setMarking(false);
                  }
                }}
                className="inline-flex items-center gap-1 rounded bg-rose-500 px-2.5 py-0.5 text-[11px] font-semibold text-zinc-950 hover:bg-rose-400 disabled:opacity-50"
              >
                {marking ? "..." : "Marchează"}
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="reviews-sync-btn"
            onClick={onSync}
            disabled={syncing}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 transition hover:border-orange-500/40 hover:text-orange-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizez..." : "Sincronizează acum"}
          </button>
          <button
            data-testid="reviews-export-btn"
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 transition hover:border-emerald-500/40 hover:text-emerald-200"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </header>

      {/* Main tabs: Recenzii (list) vs Recenzii pe produs (groups) vs eMAG Recenzii&Întrebări */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        <button
          data-testid="tab-reviews-list"
          onClick={() => setActiveTab("reviews")}
          className={`relative px-4 py-2 text-sm font-medium transition ${
            activeTab === "reviews"
              ? "text-orange-300"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii
          {activeTab === "reviews" && (
            <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-400" />
          )}
        </button>
        <button
          data-testid="tab-product-groups"
          onClick={() => setActiveTab("groups")}
          className={`relative px-4 py-2 text-sm font-medium transition ${
            activeTab === "groups"
              ? "text-orange-300"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii pe produs
          {activeTab === "groups" && (
            <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-400" />
          )}
        </button>
        <button
          data-testid="tab-emag-reviews"
          onClick={() => setActiveTab("emag")}
          className={`relative px-4 py-2 text-sm font-medium transition ${
            activeTab === "emag"
              ? "text-orange-300"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii și Întrebări eMAG
          {activeTab === "emag" && (
            <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-400" />
          )}
        </button>
        <button
          data-testid="tab-customer-reviews"
          onClick={() => setActiveTab("customer")}
          className={`relative px-4 py-2 text-sm font-medium transition ${
            activeTab === "customer"
              ? "text-amber-300"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii BRAGUS.ro
          {activeTab === "customer" && (
            <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-amber-400" />
          )}
        </button>
      </div>

      {activeTab === "groups" ? (
        <ProductGroupsTab />
      ) : activeTab === "emag" ? (
        <EmagReviewsTab />
      ) : activeTab === "customer" ? (
        <BragusReviewsTab />
      ) : (<>

      {/* Status tabs + counts */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          data-testid="reviews-filter-all"
          onClick={() => { setStatusFilter(""); setPage(1); }}
          className={`rounded-full px-3 py-1 text-xs font-medium transition ${
            statusFilter === ""
              ? "bg-orange-500/20 text-orange-200 ring-1 ring-orange-500/40"
              : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
          }`}
        >
          Toate <span className="ml-1 text-zinc-500">({grandTotal})</span>
        </button>
        {STATUS_ORDER.map((s) => {
          const meta = STATUS_META[s];
          const count = statusCounts[s] || 0;
          const active = statusFilter === s;
          const { Icon } = meta;
          return (
            <button
              key={s}
              data-testid={`reviews-filter-${s}`}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
                active
                  ? `${meta.chip} ring-1 ring-current/40`
                  : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Icon className="h-3 w-3" />
              {meta.label} <span className="ml-1 opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Search + last sync info */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="reviews-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută nume, telefon, observații..."
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 py-1.5 pl-8 pr-3 text-sm text-zinc-100 placeholder-zinc-500 outline-none focus:border-orange-500/50"
          />
        </div>
        <div className="text-[11px] text-zinc-500">
          Ultima sincronizare: {fmtDateTime(lastSyncAt)}
          {lastSummary && (
            <> · <span className="text-zinc-400">{lastSummary.fetched ?? 0}</span> rânduri totale · <span className="text-zinc-400">{lastSummary.skipped_empty_link ?? 0}</span> ignorate</>
          )}
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          data-testid="reviews-bulk-bar"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-orange-500/30 bg-orange-500/10 px-3 py-2"
        >
          <span className="text-xs font-medium text-orange-200">
            {selectedIds.size} {selectedIds.size === 1 ? "recenzie selectată" : "recenzii selectate"}
          </span>
          <span className="text-xs text-zinc-500">Schimbă status în:</span>
          {STATUS_ORDER.map((s) => {
            const meta = STATUS_META[s];
            const { Icon } = meta;
            return (
              <button
                key={s}
                data-testid={`reviews-bulk-set-${s}`}
                onClick={() => onBulkStatus(s)}
                disabled={bulkApplying}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition disabled:opacity-50 ${meta.chip} hover:brightness-125`}
              >
                <Icon className="h-3 w-3" />
                {meta.label}
              </button>
            );
          })}
          <button
            data-testid="reviews-bulk-clear"
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-[11px] text-zinc-400 hover:text-zinc-200"
          >
            Anulează selecția
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950/40">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="w-10 px-3 py-2.5 text-left">
                  <input
                    data-testid="reviews-select-all"
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-orange-500"
                  />
                </th>
                <th className="px-3 py-2.5 text-left">Nume</th>
                <th className="px-3 py-2.5 text-left">Produs</th>
                <th className="px-3 py-2.5 text-left">Telefon</th>
                <th className="px-3 py-2.5 text-left">Data apel</th>
                <th className="px-3 py-2.5 text-left">Link dovadă</th>
                <th className="px-3 py-2.5 text-left">Observații</th>
                <th className="px-3 py-2.5 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {loading && (
                <tr><td colSpan={8} className="px-3 py-12 text-center text-xs text-zinc-500">Se încarcă...</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-12 text-center text-xs text-zinc-500">
                    <AlertCircle className="mx-auto mb-2 h-5 w-5 text-zinc-700" />
                    Nicio recenzie de afișat.
                    <p className="mt-1 text-[10px]">Doar rândurile cu „Link dovada recenzie&rdquo; completat în Google Sheet sunt importate.</p>
                  </td>
                </tr>
              )}
              {!loading && items.map((r) => {
                const selected = selectedIds.has(r.id);
                return (
                  <tr
                    key={r.id}
                    data-testid={`reviews-row-${r.id}`}
                    className={`transition hover:bg-zinc-900/40 ${selected ? "bg-orange-500/5" : ""}`}
                  >
                    <td className="px-3 py-2.5">
                      <input
                        data-testid={`reviews-select-${r.id}`}
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOne(r.id)}
                        className="h-4 w-4 cursor-pointer accent-orange-500"
                      />
                    </td>
                    <td className="px-3 py-2.5 text-zinc-100">{r.nume || <span className="text-zinc-600">—</span>}</td>
                    <td className="max-w-[280px] px-3 py-2.5 text-zinc-200" title={r.product_name || ""}>
                      {r.product_name ? (
                        <div className="flex items-start gap-1.5">
                          {r.review_url ? (
                            <a
                              href={r.review_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`reviews-product-link-${r.id}`}
                              className="line-clamp-2 text-xs text-zinc-200 hover:text-orange-300 hover:underline"
                            >
                              {r.product_name}
                            </a>
                          ) : (
                            <span className="line-clamp-2 text-xs">{r.product_name}</span>
                          )}
                          {r.product_pnk && (
                            <span className="shrink-0 rounded bg-zinc-800/80 px-1.5 py-0.5 font-mono text-[9px] text-zinc-400" title="eMAG PNK">{r.product_pnk}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs text-zinc-300">{r.telefon || "—"}</td>
                    <td className="px-3 py-2.5 text-zinc-400">{r.data_apel || "—"}</td>
                    <td className="px-3 py-2.5">
                      {r.link_dovada ? (
                        <a
                          href={r.link_dovada}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`reviews-link-${r.id}`}
                          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200 hover:underline"
                          title={r.link_dovada}
                        >
                          <ExternalLink className="h-3 w-3" />
                          Deschide
                        </a>
                      ) : "—"}
                    </td>
                    <td className="max-w-[260px] truncate px-3 py-2.5 text-zinc-300" title={r.observatii || ""}>
                      {r.observatii || <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <StatusChip status={r.status} />
                        <select
                          data-testid={`reviews-status-select-${r.id}`}
                          value={r.status}
                          onChange={(e) => onChangeStatus(r.id, e.target.value)}
                          className="rounded-md border border-zinc-800 bg-zinc-900 px-1.5 py-0.5 text-[11px] text-zinc-300 outline-none hover:border-orange-500/40"
                        >
                          {STATUS_ORDER.map((s) => (
                            <option key={s} value={s}>{STATUS_META[s].label}</option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-900 bg-zinc-950/60 px-3 py-2"
          data-testid="reviews-pagination">
          <p className="text-[11px] text-zinc-500">
            {total === 0 ? "Nicio recenzie" : (
              <>Afișare <span className="font-semibold text-zinc-300">{(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)}</span> din <span className="font-semibold text-zinc-300">{total}</span> · Pagina <span className="text-orange-300">{page}</span>/<span className="text-zinc-400">{totalPages}</span></>
            )}
          </p>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                data-testid="reviews-first-page"
                onClick={() => setPage(1)} disabled={page <= 1}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:border-orange-500/40 disabled:opacity-40">« Prima</button>
              <button
                data-testid="reviews-prev-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="rounded-md border border-zinc-800 bg-zinc-900 p-1.5 text-zinc-300 hover:border-orange-500/40 disabled:opacity-40">
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              {/* Numbered pages (windowed) */}
              {(() => {
                const nums = [];
                let s = Math.max(1, page - 2), e = Math.min(totalPages, page + 2);
                if (page <= 3) e = Math.min(totalPages, 5);
                if (page >= totalPages - 2) s = Math.max(1, totalPages - 4);
                for (let i = s; i <= e; i++) nums.push(i);
                return nums.map((n) => (
                  <button key={n} onClick={() => setPage(n)}
                    data-testid={`reviews-page-${n}`}
                    className={`min-w-[28px] rounded-md border px-2 py-1 text-xs transition ${
                      n === page ? "border-orange-500/60 bg-orange-500/15 text-orange-200 font-semibold" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-orange-500/40"
                    }`}>{n}</button>
                ));
              })()}
              <button
                data-testid="reviews-next-page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="rounded-md border border-zinc-800 bg-zinc-900 p-1.5 text-zinc-300 hover:border-orange-500/40 disabled:opacity-40">
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid="reviews-last-page"
                onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                className="rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1 text-xs text-zinc-300 hover:border-orange-500/40 disabled:opacity-40">Ultima »</button>
            </div>
          )}
        </div>
      </div>
      </>)}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
// eMAG Reviews & Questions Tab
// ═══════════════════════════════════════════════════════════════════

function EmagReviewsTab() {
  const [subTab, setSubTab] = useState("reviews"); // reviews | questions
  const [reviewSubTab, setReviewSubTab] = useState("positive"); // positive | negative
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [extractingId, setExtractingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/emag-reviews/families`);
      setFamilies(data.families || []);
    } catch (e) {
      toast.error("Nu am putut încărca familiile", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const extract = async (family_id) => {
    setExtractingId(family_id);
    try {
      const { data } = await axios.post(`${API}/emag-reviews/family/${family_id}/extract`, {}, { timeout: 180000 });
      const rv = data.reviews || {}; const q = data.questions || {};
      toast.success("Extracție completă", {
        description: `${rv.total_count || 0} recenzii · ${rv.new_reviews_added || 0} noi · ${q.total_count || 0} întrebări · ${q.new_questions_added || 0} noi${rv.first_extract ? " · PRIMA EXTRACȚIE (cutoff setat)" : ""}`,
      });
      load();
    } catch (e) {
      toast.error("Extracție eșuată", { description: e.response?.data?.detail || e.message });
    } finally { setExtractingId(null); }
  };

  // Bulk-extract state — used by the 3 top buttons (positives/negatives/
  // questions). All three hit the same endpoint since `sync_all_families`
  // pulls reviews + questions atomically; the label distinction is a UX
  // affordance so the operator knows which bucket they're refreshing
  // before viewing it below.
  const [bulkBusy, setBulkBusy] = useState(null); // "positive" | "negative" | "questions" | null
  const runBulkExtract = async (kind) => {
    if (bulkBusy) return;
    setBulkBusy(kind);
    // Cloudflare/proxy cuts the connection at 120s. The full pipeline
    // (extract-all-families + email) takes 2–5 min on production, so we
    // split it in two calls:
    //   1) Kick off extract-all-families as "fire-and-forget" (short
    //      timeout — even if the client aborts, the backend keeps running).
    //   2) Immediately call the digest email endpoint using whatever is
    //      currently in DB (the automated scheduler keeps this fresh).
    // For POSITIVE we just want the extract fresh — no email send.
    const toastId = toast.loading(
      kind === "positive" ? "Extrag recenzii pozitive..."
      : kind === "negative" ? "Pornesc scanare LIVE eMAG (negative fără răspuns)..."
      : "Pornesc scanare LIVE eMAG (întrebări fără răspuns)...",
      { description: kind === "positive"
          ? "Poate dura până la 2 minute — se scanează toate familiile pe eMAG."
          : "Cererea revine imediat; scanul (5 pagini × toate familiile) + emailul rulează în background." }
    );
    try {
      if (kind === "positive") {
        console.log(`[Reviews] runBulkExtract kind=positive — POST /extract-all-families`);
        const { data } = await axios.post(`${API}/emag-reviews/extract-all-families`, {}, { timeout: 180000 });
        const t = data.totals || {};
        toast.dismiss(toastId);
        toast.success(`Extracție pozitive (${t.families_ok}/${t.families} familii)`, {
          description: `${t.reviews_total} recenzii · ${t.reviews_new} noi (${t.positives_new} pozitive)`,
        });
        setSubTab("reviews"); setReviewSubTab("positive");
        await load();
        return;
      }

      // Fire-and-forget LIVE scan endpoints — returnează 202 imediat, scan
      // + email se întâmplă server-side în background (2-3 min). Elimină
      // complet timeout-ul de 120s al Cloudflare pe producție.
      const isNeg = kind === "negative";
      const endpoint = isNeg
        ? `${API}/emag-reviews/negatives-live-scan-email`
        : `${API}/emag-reviews/questions-live-scan-email`;
      console.log(`[Reviews] POST ${endpoint} (fire-and-forget)`);
      const { data: mailRes } = await axios.post(endpoint,
        { only_unanswered: true },
        { timeout: 15000 });  // 202 arrives in < 100 ms
      console.log(`[Reviews] live-scan started`, mailRes);
      const label = isNeg ? "Recenzii negative fără răspuns" : "Întrebări fără răspuns";
      toast.dismiss(toastId);
      toast.success(`Scanare LIVE pornită · ${label}`, {
        description: "Se scanează în background toate familiile pe eMAG (5 pagini/familie). Emailul cu Excel-ul va sosi în 2-3 minute pe adresa configurată.",
        duration: 20000,
      });
      if (kind === "questions") setSubTab("questions");
      else { setSubTab("reviews"); setReviewSubTab("negative"); }
      await load();
    } catch (e) {
      console.error("[Reviews] runBulkExtract failed", e);
      toast.dismiss(toastId);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.response?.data?.message;
      const isTimeout = e.code === "ECONNABORTED" || /timeout/i.test(String(e.message || ""));
      const isProxyTimeout = /520|522|524|origin web server/i.test(String(e.response?.data || e.message || ""));
      let msg = "Operațiune eșuată";
      let desc = "";
      if (isTimeout || isProxyTimeout) {
        msg = "Timeout — serverul a durat prea mult";
        desc = "Extracția rulează în continuare pe server (automat, la fiecare 30 min). Așteaptă și reîncearcă butonul de email peste 1-2 minute.";
      } else if (status === 401 || status === 403) {
        msg = "Neautentificat / neautorizat";
        desc = "Sesiune expirată. Fă logout + login.";
      } else if (status === 500) {
        msg = "Eroare server 500";
        desc = detail || e.message || "Verifică logurile backend.";
      } else if (detail) {
        desc = detail;
      } else if (e.message) {
        desc = e.message;
      }
      toast.error(msg, { description: desc, duration: 20000 });
    } finally { setBulkBusy(null); }
  };

  // Pipeline runs fire-and-forget on the backend (bypasses Cloudflare 120s
  // timeout on production). We POST to /nightly-pipeline/start → get 202 in
  // < 100 ms, then poll GET /nightly-pipeline/status every 15 s until the
  // job reports running:false. State is server-persisted so refreshes /
  // page reopens still show live progress.
  const [pipelineStatus, setPipelineStatus] = useState(null); // null | { running, started_at, scope, error, finished_at, last_result }
  const [pipelineFamId, setPipelineFamId] = useState(null);
  const pipelineInFlightRef = useRef(false);
  const runningPipeline = !!(pipelineStatus && pipelineStatus.running);

  const refreshPipelineStatus = useCallback(async () => {
    try {
      // Cache-bust: proxy/browser poate cache-ui GET-ul si sa returneze
      // "running:true" chiar dupa ce backend-ul a scris "running:false".
      const { data } = await axios.get(
        `${API}/emag-reviews/nightly-pipeline/status?_ts=${Date.now()}`,
        { headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" } },
      );
      setPipelineStatus(data || null);
      return data || null;
    } catch (e) {
      console.warn("[Reviews] pipeline status fetch failed", e?.message);
      return null;
    }
  }, []);

  const resetPipelineStatus = async () => {
    if (!window.confirm(
      "Marchez pipeline-ul curent ca finalizat.\n\n" +
      'Folosește-l DOAR dacă banner-ul e blocat pe „rulează" după un restart de backend.\n' +
      "Nu re-trimite emailuri, nu re-postează Gomag — doar resetează starea vizuală. Continui?"
    )) return;
    try {
      await axios.post(`${API}/emag-reviews/nightly-pipeline/reset`, {});
      toast.success("Status pipeline resetat");
      await refreshPipelineStatus();
    } catch (e) {
      toast.error("Reset eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  // Initial fetch on mount.
  useEffect(() => {
    refreshPipelineStatus();
  }, [refreshPipelineStatus]);

  // Poll every 10s WHILE the pipeline is running. Effect re-runs when
  // `running` flips (start of run → begins polling; end of run → stops).
  // Fix 2026-02: inainte polling-ul rula doar 1x la mount cand `running`
  // era false, deci UI-ul nu vedea niciodata `running: true → false`
  // decat cu F5 pe pagina.
  useEffect(() => {
    if (!pipelineStatus?.running) return;
    const interval = setInterval(() => {
      refreshPipelineStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [pipelineStatus?.running, refreshPipelineStatus]);

  // When a running job flips to finished, show a summary toast + refresh page data.
  const prevRunningRef = useRef(false);
  useEffect(() => {
    const wasRunning = prevRunningRef.current;
    const isRunning = !!(pipelineStatus && pipelineStatus.running);
    if (wasRunning && !isRunning && pipelineStatus) {
      const r = pipelineStatus.last_result || {};
      if (pipelineStatus.error) {
        toast.error("Pipeline eșuat", { description: pipelineStatus.error, duration: 20000 });
      } else {
        toast.success(`Pipeline ${pipelineStatus.scope || ""} — DONE`, {
          description: `Scanate: ${r.scanned ?? "?"}/${r.families ?? "?"} · eMAG RPA: ${r.rpa_ok ? `OK (${r.rpa_replies || 0} răspunsuri)` : "eroare"} · Gomag jobs: ${r.gomag_jobs ?? 0} · Email: ${(r.email_recipients || []).length} destinatari`,
          duration: 20000,
        });
      }
      load().catch(() => {});
    }
    prevRunningRef.current = isRunning;
  }, [pipelineStatus]);

  const runNightlyPipeline = async (familyId = null, familyName = null) => {
    if (runningPipeline || pipelineInFlightRef.current) return;
    pipelineInFlightRef.current = true;
    const scope = familyId
      ? `pentru familia „${familyName || familyId}"`
      : "pentru TOATE familiile";
    const ok = window.confirm(
      `Rulez PIPELINE-UL COMPLET ${scope}:\n\n` +
      "1) Scanez recenzii pozitive + negative din eMAG (100 per familie)\n" +
      "2) Răspund pe eMAG (RPA) la pozitivele fără răspuns cu textul BRAGUS\n" +
      "3) Trimit la Gomag + răspund pe Gomag cu textul din „Răspuns Recenzii Gomag\"\n" +
      "4) Trimit email cu raport la finalul rulării\n\n" +
      "Durează câteva minute — rulează în background. Poți închide tab-ul, statusul apare oricând pe pagină. Continui?"
    );
    if (!ok) { pipelineInFlightRef.current = false; return; }
    setPipelineFamId(familyId);
    try {
      const body = familyId ? { family_id: familyId } : {};
      // Fire-and-forget: returns 202 in < 100 ms. Cloudflare-safe.
      await axios.post(`${API}/emag-reviews/nightly-pipeline/start`, body, { timeout: 15000 });
      toast.success("Pipeline pornit în background", {
        description: `Scop: ${familyId ? "familie " + (familyName || familyId) : "toate familiile"}. Vezi progresul live pe pagină.`,
        duration: 8000,
      });
      // Immediately refresh status so the banner shows running:true.
      await refreshPipelineStatus();
    } catch (e) {
      toast.error("Nu am putut porni pipeline-ul", { description: e.response?.data?.detail || e.message });
    } finally { pipelineInFlightRef.current = false; }
  };

  // Removed 2026-02-04 per user request:
  //   - "Trimite digest acum" (sendTestDigest)
  //   - "Trimite negative (Excel)" (sendNegativesExcel)
  //   - "Trimite întrebări (Excel)" (sendQuestionsExcel)
  //   - "Trimite pozitive → Gomag" (sendGomagPositives)
  // The bulk-extract Negatives / Questions buttons now trigger the same
  // email + Excel flow inline. Pipeline auto covers positives → Gomag.


  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Recenzii și Întrebări eMAG</h2>
          <p className="text-xs text-zinc-500">
            Extrase din eMAG public. Sync săptămânal automat (luni 09:00 RO). Prima extracție per familie setează „cutoff-ul” — orice apare după devine „NOU” pentru digest-ul de luni.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="emag-reviews-reload-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs font-medium text-zinc-200 hover:border-orange-500/40 disabled:opacity-50">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Bulk-extract shortcuts. Positive = scan only (50/fam). Negative &
          Questions = scan (50/fam) + trimite email cu Excel atașat pt tab-ul
          respectiv, filtrat la "fără răspuns". */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-2.5">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500 mr-1">Extrage pentru toate familiile:</span>
        <button
          data-testid="emag-bulk-positive"
          onClick={() => runBulkExtract("positive")}
          disabled={!!bulkBusy}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 px-3 py-1.5 text-xs font-semibold text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-50"
          title="Scan 50 recenzii/familie — doar populează DB (fără email)"
        >
          {bulkBusy === "positive" ? "Se extrage..." : "★ Recenzii pozitive (scan)"}
        </button>
        <button
          data-testid="emag-bulk-negative"
          onClick={() => runBulkExtract("negative")}
          disabled={!!bulkBusy}
          className="inline-flex items-center gap-1.5 rounded-md bg-rose-500/20 border border-rose-500/40 px-3 py-1.5 text-xs font-semibold text-rose-100 hover:bg-rose-500/30 disabled:opacity-50"
          title="Scan 50 recenzii/familie + trimite email cu Excel (tab 2: negative fără răspuns)"
        >
          {bulkBusy === "negative" ? "Se extrage + trimite..." : "☆ Negative → Email Excel"}
        </button>
        <button
          data-testid="emag-bulk-questions"
          onClick={() => runBulkExtract("questions")}
          disabled={!!bulkBusy}
          className="inline-flex items-center gap-1.5 rounded-md bg-sky-500/20 border border-sky-500/40 px-3 py-1.5 text-xs font-semibold text-sky-100 hover:bg-sky-500/30 disabled:opacity-50"
          title="Scan 50 întrebări/familie + trimite email cu Excel (tab 3: întrebări fără răspuns)"
        >
          {bulkBusy === "questions" ? "Se extrage + trimite..." : "? Întrebări → Email Excel"}
        </button>
        <span className="text-[10px] text-zinc-500 ml-2">
          Pozitive: doar scan. Negative/Întrebări: scan + email cu Excel pentru cele fără răspuns.
        </span>
      </div>

      {/* Pipeline auto complet — user-requested one-click button:
          scan (paginated 500/fam) → RPA reply on eMAG for unanswered
          positives → forward + reply on Gomag → refresh counters. Same
          as the 03:00 Bucharest cronjob but on-demand. */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5">
        <span className="text-[11px] uppercase tracking-wider text-amber-200 mr-1">Pipeline auto (pozitive):</span>
        <button
          data-testid="emag-run-nightly-pipeline"
          onClick={() => runNightlyPipeline()}
          disabled={runningPipeline}
          className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 border border-amber-400 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400 disabled:opacity-50"
        >
          {runningPipeline && !pipelineFamId ? "Rulează în background..." : "⚡ Toate familiile: Scanează → Răspunde eMAG → Trimite/răspunde Gomag"}
        </button>
        <span className="text-[10px] text-amber-100/80 ml-2">
          Rulare completă a fluxului cerut: 500 recenzii/familie · text standard BRAGUS · actualizează contorii peste tot
        </span>
      </div>

      {/* Live pipeline status banner — fed by GET /nightly-pipeline/status
          polled every 15 s while running. Persistent across page reloads. */}
      {pipelineStatus && (pipelineStatus.running || pipelineStatus.finished_at) && (
        <div
          data-testid="emag-pipeline-status-banner"
          className={`rounded-lg border p-3 text-xs ${
            pipelineStatus.running
              ? "border-sky-500/40 bg-sky-500/10 text-sky-100"
              : pipelineStatus.error
              ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
              : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-semibold">
              {pipelineStatus.running ? (
                <>
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  <span>Pipeline rulează în background · {pipelineStatus.scope || "—"}</span>
                </>
              ) : pipelineStatus.error ? (
                <>
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>Ultima rulare a eșuat · {pipelineStatus.scope || "—"}</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Ultima rulare finalizată cu succes · {pipelineStatus.scope || "—"}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshPipelineStatus}
                className="rounded border border-white/20 px-2 py-0.5 text-[10px] hover:bg-white/10"
                data-testid="emag-pipeline-status-refresh"
              >
                Reîmprospătează
              </button>
              {pipelineStatus.running && (
                <button
                  onClick={resetPipelineStatus}
                  className="rounded border border-rose-500/40 px-2 py-0.5 text-[10px] text-rose-100 hover:bg-rose-500/20"
                  data-testid="emag-pipeline-status-reset"
                  title="Marchează pipeline-ul ca finalizat (folosește doar dacă e blocat)"
                >
                  Resetează
                </button>
              )}
            </div>
          </div>
          <div className="mt-1.5 grid grid-cols-1 gap-x-4 gap-y-0.5 text-[11px] opacity-90 sm:grid-cols-2">
            {pipelineStatus.started_at && (
              <div>Pornit: <span className="font-mono">{fmtDateTime(pipelineStatus.started_at)}</span></div>
            )}
            {pipelineStatus.finished_at && (
              <div>Finalizat: <span className="font-mono">{fmtDateTime(pipelineStatus.finished_at)}</span></div>
            )}
            {pipelineStatus.last_result && (
              <>
                <div>Familii scanate: <span className="font-mono">{pipelineStatus.last_result.scanned ?? "?"} / {pipelineStatus.last_result.families ?? "?"}</span></div>
                <div>eMAG RPA: <span className="font-mono">{pipelineStatus.last_result.rpa_ok ? `OK (${pipelineStatus.last_result.rpa_replies || 0} răspunsuri)` : "eșec"}</span></div>
                <div>Gomag jobs: <span className="font-mono">{pipelineStatus.last_result.gomag_jobs ?? 0}</span></div>
                <div>Email trimis către: <span className="font-mono">{(pipelineStatus.last_result.email_recipients || []).length} destinatari</span></div>
              </>
            )}
            {pipelineStatus.error && (
              <div className="col-span-full break-all">Eroare: <span className="font-mono">{pipelineStatus.error}</span></div>
            )}
            {pipelineStatus.running && (
              <div className="col-span-full opacity-70">Statusul se reîmprospătează automat la 15 secunde. Poți închide pagina — pipeline-ul continuă pe server.</div>
            )}
          </div>
        </div>
      )}

      {/* Sub-tabs: Recenzii vs Întrebări */}
      <div className="flex items-center gap-1 border-b border-zinc-800">
        {[
          { k: "reviews", label: "Recenzii" },
          { k: "questions", label: "Întrebări" },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`emag-subtab-${t.k}`}
            onClick={() => setSubTab(t.k)}
            className={`relative px-4 py-2 text-sm font-medium transition ${
              subTab === t.k ? "text-orange-300" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {t.label}
            {subTab === t.k && (
              <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-orange-400" />
            )}
          </button>
        ))}
      </div>

      {subTab === "reviews" && (
        <>
          {/* Positive / Negative sub-sub-tabs */}
          <div className="flex items-center gap-2">
            {[
              { k: "positive", label: "Pozitive (4-5★)", color: "emerald" },
              { k: "negative", label: "Negative (1-2-3★)", color: "red" },
            ].map((t) => (
              <button
                key={t.k}
                data-testid={`emag-review-sentiment-${t.k}`}
                onClick={() => setReviewSubTab(t.k)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                  reviewSubTab === t.k
                    ? t.color === "emerald"
                      ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-500/40"
                      : "bg-red-500/20 text-red-200 ring-1 ring-red-500/40"
                    : "bg-zinc-900 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && <p className="text-sm text-zinc-500">Se încarcă...</p>}

          {!loading && families.length === 0 && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-8 text-center">
              <Star className="mx-auto h-8 w-8 text-zinc-600" />
              <p className="mt-2 text-sm text-zinc-400">Nicio familie configurată încă.</p>
              <p className="mt-1 text-xs text-zinc-500">Adaugă familii în tabul „Recenzii pe produs”.</p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {families.map((f) => (
              <EmagFamilyReviewCard
                key={f.family_id}
                family={f}
                sentiment={reviewSubTab}
                onExtract={() => extract(f.family_id)}
                extracting={extractingId === f.family_id}
                onReload={load}
                runPipeline={runNightlyPipeline}
                pipelineBusy={runningPipeline}
              />
            ))}
          </div>
        </>
      )}

      {subTab === "questions" && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {families.map((f) => (
            <EmagFamilyQuestionCard
              key={f.family_id}
              family={f}
              onExtract={() => extract(f.family_id)}
              extracting={extractingId === f.family_id}
              onReload={load}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EmagFamilyReviewCard({ family, sentiment, onExtract, extracting, onReload, runPipeline, pipelineBusy }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const isPositive = sentiment === "positive";
  const captured = family.captured || {};
  const rd = family.rating_distribution || {};
  const totalForSentiment = isPositive
    ? (Number(rd["4"] || 0) + Number(rd["5"] || 0))
    : (Number(rd["1"] || 0) + Number(rd["2"] || 0) + Number(rd["3"] || 0));
  const capturedTotal = isPositive ? captured.positive : captured.negative;
  const responded = isPositive ? captured.positive_responded : captured.negative_responded;
  const notResponded = isPositive ? captured.positive_not_responded : captured.negative_not_responded;
  const newCount = isPositive ? captured.positive_new : captured.negative_new;

  const loadItems = useCallback(async () => {
    setBusy(true);
    try {
      const endpoint = isPositive ? "positives" : "negatives";
      const { data } = await axios.get(`${API}/emag-reviews/family/${family.family_id}/${endpoint}`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Eroare încărcare", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }, [family.family_id, isPositive]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && items === null) loadItems();
  };

  const markGomagSent = async (reviewId) => {
    try {
      await axios.post(`${API}/emag-reviews/review/${reviewId}/mark-gomag-sent`);
      toast.success("Marcată ca trimisă la Gomag (soft — nu s-a trimis efectiv).");
      loadItems(); onReload();
    } catch (e) {
      toast.error("Eroare", { description: e.response?.data?.detail || e.message });
    }
  };

  const markReviewAnswered = async (reviewId) => {
    if (!window.confirm("Marchez această recenzie ca având răspuns pe eMAG? (Folosește când ai răspuns pe eMAG.ro dar scraper-ul nu detectează.)")) return;
    try {
      await axios.post(`${API}/emag-reviews/review/${reviewId}/mark-answered`, {});
      toast.success("Marcată ca răspunsă.");
      loadItems(); onReload();
    } catch (e) {
      toast.error("Eroare", { description: e.response?.data?.detail || e.message });
    }
  };

  const markAllPositivesGomag = async () => {
    if (!window.confirm(`Marchez toate recenziile pozitive capturate din „${family.name}” ca trimise la Gomag? (soft, fără trimitere reală)`)) return;
    try {
      const { data } = await axios.post(`${API}/emag-reviews/family/${family.family_id}/mark-all-positives-gomag-sent`);
      toast.success(`${data.marked || 0} recenzii marcate.`);
      loadItems(); onReload();
    } catch (e) {
      toast.error("Eroare", { description: e.response?.data?.detail || e.message });
    }
  };

  const sendRecentToGomag = async () => {
    const label = isPositive ? "pozitive" : "negative";
    const raw = window.prompt(
      `Câte dintre cele mai recente recenzii ${label} din „${family.name}" să marchez ca trimise la Gomag? (1–500, soft)`,
      "5",
    );
    if (raw == null) return;
    const n = parseInt(raw.trim(), 10);
    if (!Number.isFinite(n) || n <= 0 || n > 500) {
      toast.error("Introdu un număr valid între 1 și 500.");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/emag-reviews/family/${family.family_id}/mark-recent-gomag-sent`,
        { sentiment: isPositive ? "positive" : "negative", limit: n },
      );
      const marked = data.marked || 0;
      const requested = data.requested || n;
      if (marked === 0) {
        toast.info(`Nu sunt recenzii ${label} netrimise (cerute: ${requested}).`);
      } else if (marked < requested) {
        toast.success(`${marked} recenzii marcate (ai cerut ${requested}, dar restul erau deja trimise).`);
      } else {
        toast.success(`${marked} recenzii ${label} marcate ca trimise la Gomag.`);
      }
      loadItems(); onReload();
    } catch (e) {
      toast.error("Eroare", { description: e.response?.data?.detail || e.message });
    }
  };

  const sendRecentRealGomag = async () => {
    const label = isPositive ? "pozitive" : "negative";
    const raw = window.prompt(
      `Câte recenzii ${label} REALE să trimit la Gomag pentru „${family.name}"? (1–100)\n\nATENȚIE: Gomag are rate-limit 1 recenzie/sec și se trimite câte una pentru fiecare SKU din familie. Fiecare recenzie ajunge LIVE pe magazin.`,
      "5",
    );
    if (raw == null) return;
    const n = parseInt(raw.trim(), 10);
    if (!Number.isFinite(n) || n <= 0 || n > 100) {
      toast.error("Introdu un număr valid între 1 și 100.");
      return;
    }
    if (!window.confirm(`Confirmi trimiterea REALĂ a ${n} recenzii ${label} către Gomag pentru „${family.name}"?\n\nRecenziile vor deveni PUBLICE pe magazin după procesarea la Gomag.`)) return;
    try {
      const { data } = await axios.post(
        `${API}/emag-reviews/family/${family.family_id}/send-recent-to-gomag`,
        { sentiment: isPositive ? "positive" : "negative", limit: n },
        { timeout: 120000 },
      );
      const sent = data.sent || 0;
      const attempted = data.attempted || 0;
      const failed = data.failed || [];
      if (sent === 0 && attempted === 0) {
        toast.info(`Nu sunt recenzii ${label} netrimise.`);
      } else if (failed.length === 0) {
        toast.success(`${sent} recenzii ${label} trimise LIVE la Gomag (${attempted} POST-uri, 0 erori).`);
      } else {
        toast.warning(`${sent}/${n} recenzii trimise, ${failed.length} erori pe unele SKU-uri (vezi consola).`);
        console.warn("Gomag review send failures:", failed);
      }
      loadItems(); onReload();
    } catch (e) {
      toast.error("Trimitere Gomag eșuată", { description: e.response?.data?.detail || e.message });
    }
  };

  const exportNegativesXlsx = async () => {
    try {
      const r = await axios.get(
        `${API}/emag-reviews/family/${family.family_id}/negatives/export`,
        { responseType: "blob", timeout: 60000 },
      );
      // Derive filename from Content-Disposition header if present.
      const disp = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disp);
      const filename = match ? match[1] : `recenzii_negative_${family.name || family.family_id}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel descărcat: ${filename}`);
    } catch (e) {
      toast.error("Export eșuat", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className={`rounded-lg border p-4 ${isPositive ? "border-emerald-500/20 bg-emerald-500/5" : "border-red-500/20 bg-red-500/5"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-zinc-100" title={family.name}>{family.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {(family.skus || []).map((s) => (
              <span key={s} className="rounded bg-zinc-800 px-1 py-0.5 text-[9px] font-mono text-zinc-300">{s}</span>
            ))}
            <FamilyEmagLink pnks={family.pnks} testid={`emag-review-family-${family.family_id}-link`} />
          </div>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-bold ${isPositive ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" : "border-red-500/40 bg-red-500/10 text-red-200"}`}>
          {totalForSentiment}
        </span>
      </div>

      {/* Stats grid */}
      <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
        <div className="rounded border border-zinc-800 bg-zinc-950/70 px-1 py-1" title="Recenzii capturate în DB (subset din latest 10, crește săptămânal)">
          <div className="text-[9px] uppercase text-zinc-500">Capturate</div>
          <div className="font-mono text-sm font-bold text-zinc-200">{capturedTotal || 0}</div>
        </div>
        <div className="rounded border border-sky-500/30 bg-sky-500/5 px-1 py-1" title="Din capturate — recenzii cu răspuns BRAGUS">
          <div className="text-[9px] uppercase text-sky-300/80">Cu răsp.</div>
          <div className="font-mono text-sm font-bold text-sky-300">{responded || 0}</div>
        </div>
        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-1 py-1" title="Din capturate — recenzii FĂRĂ răspuns BRAGUS (te duci să răspunzi manual)">
          <div className="text-[9px] uppercase text-amber-300/80">Fără răsp.</div>
          <div className="font-mono text-sm font-bold text-amber-300">{notResponded || 0}</div>
        </div>
      </div>

      {newCount > 0 && (
        <div className="mt-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] text-orange-200">
          <strong className="font-bold">{newCount}</strong> {isPositive ? "pozitive" : "negative"} noi (după cutoff)
        </div>
      )}

      {family.reviews_last_error && (
        <div
          data-testid={`emag-family-error-${family.family_id}`}
          className="mt-2 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200"
          title={family.reviews_last_error}
        >
          <div className="flex items-center gap-1 font-semibold">
            <AlertTriangle className="h-3 w-3" />
            Eroare ultimă extragere
            {family.reviews_last_failed_at && (
              <span className="ml-auto text-[9px] font-normal text-rose-300/70">
                {new Date(family.reviews_last_failed_at).toLocaleString("ro-RO")}
              </span>
            )}
          </div>
          <div className="mt-0.5 line-clamp-2 text-rose-100/90">{family.reviews_last_error}</div>
        </div>
      )}

      {isPositive && (
        <div className="mt-2 flex items-center justify-between rounded border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 text-[11px]">
          <span className="text-emerald-300/80">Gomag:</span>
          <span className="font-mono text-emerald-200">
            {captured.positive_gomag_sent || 0} trimise · {captured.positive_gomag_not_sent || 0} netrimise
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {/* Extract button ONLY on positives — a single scan (5 pages × 10 items = 50 reviews)
            populates BOTH positives and negatives in DB. No need to duplicate on negatives tab. */}
        {isPositive && (
          <button
            data-testid={`emag-extract-${family.family_id}`}
            onClick={onExtract}
            disabled={extracting}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] font-medium text-zinc-100 hover:border-orange-500/40 disabled:opacity-50">
            <RefreshCw className={`h-3 w-3 ${extracting ? "animate-spin" : ""}`} />
            {extracting ? "Extrag..." : "Extrage recenzii"}
          </button>
        )}
        {!isPositive && (
          <span className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] italic text-zinc-500">
            Extragerea se face din tabul <span className="font-medium text-zinc-400">Pozitive</span> (un singur scan pentru ambele)
          </span>
        )}
        {!isPositive && (
          <button
            data-testid={`emag-toggle-${family.family_id}-${sentiment}`}
            onClick={toggleExpand}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] font-medium text-zinc-100 hover:border-orange-500/40">
            {expanded ? "Ascunde" : "Vezi lista"}
          </button>
        )}
        {!isPositive && (
          <button
            data-testid={`emag-export-negatives-${family.family_id}`}
            onClick={exportNegativesXlsx}
            className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-200 hover:bg-red-500/20">
            <Download className="h-3 w-3" />
            Exportă Excel
          </button>
        )}
        {isPositive && (
          <span className="inline-flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-950 px-2 py-1 text-[10px] italic text-zinc-500">
            Trimiterea/răspunsul pe Gomag se face din <span className="font-medium text-zinc-400">Pipeline familie</span> sau săptămânal automat luni 09:00.
          </span>
        )}
        {isPositive && runPipeline && (
          <button
            data-testid={`emag-run-pipeline-${family.family_id}`}
            onClick={() => runPipeline(family.family_id, family.name)}
            disabled={pipelineBusy}
            className="inline-flex items-center gap-1 rounded-md border border-amber-400 bg-amber-500 px-2 py-1 text-[11px] font-bold text-black hover:bg-amber-400 disabled:opacity-50"
            title="Rulează pipeline complet doar pentru această familie (scan + eMAG RPA + Gomag + email)">
            ⚡ Pipeline familie
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {busy && <p className="text-xs text-zinc-500">Încărcare...</p>}
          {!busy && items && items.length === 0 && (
            <p className="text-xs text-zinc-500">Nicio recenzie {isPositive ? "pozitivă" : "negativă"} capturată încă.</p>
          )}
          {!busy && items && items.map((r) => (
            <div key={r.id} className="rounded border border-zinc-800 bg-zinc-950/60 p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  <span className={`font-mono font-bold ${r.rating >= 4 ? "text-emerald-300" : "text-red-300"}`}>
                    {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                  </span>
                  <span className="text-[10px] text-zinc-500">· {r.user_name || "—"}</span>
                </div>
                <span className="text-[10px] text-zinc-500">{fmtDateTime(r.created_at_emag)}</span>
              </div>
              {r.title && <div className="mt-1 font-semibold text-zinc-100">{r.title}</div>}
              {r.content && <div className="mt-1 text-zinc-300 whitespace-pre-wrap">{r.content}</div>}
              <div className="mt-2 flex items-center gap-2">
                {r.has_vendor_reply ? (
                  <span className="inline-flex items-center gap-1 rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-300">
                    <CheckCircle2 className="h-3 w-3" /> Bragus a răspuns
                  </span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] text-amber-300">
                      <AlertCircle className="h-3 w-3" /> Fără răspuns
                    </span>
                    <button
                      data-testid={`emag-review-mark-answered-${r.id}`}
                      onClick={() => markReviewAnswered(r.id)}
                      title="Marchează manual ca având răspuns pe eMAG (pentru cazul în care scraper-ul nu detectează)"
                      className="rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-200 hover:bg-sky-500/20"
                    >
                      Marchează cu răspuns
                    </button>
                  </>
                )}
                {r.review_url && (
                  <a href={`https://www.emag.ro${r.review_url}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-orange-300">
                    <ExternalLink className="h-3 w-3" /> pe eMAG
                  </a>
                )}
                <span className="ml-auto flex items-center gap-1">
                  {r.gomag_sent ? (
                    <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">Gomag ✓</span>
                  ) : (
                    <button
                      data-testid={`emag-review-gomag-${r.id}`}
                      onClick={() => {
                        if (!isPositive && !window.confirm("Trimit această recenzie negativă la Gomag? (soft, fără trimitere reală)")) return;
                        markGomagSent(r.id);
                      }}
                      className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-500/20">
                      Trimite Gomag
                    </button>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmagFamilyQuestionCard({ family, onExtract, extracting, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const q = family.questions || {};

  const loadItems = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/emag-reviews/family/${family.family_id}/questions`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Eroare încărcare", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  }, [family.family_id]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && items === null) loadItems();
  };

  const markGomag = async (qid) => {
    if (!window.confirm("Trimit această întrebare la Gomag? (soft, fără trimitere reală)")) return;
    try {
      await axios.post(`${API}/emag-reviews/question/${qid}/mark-gomag-sent`);
      toast.success("Marcată ca trimisă (soft).");
      loadItems(); onReload();
    } catch (e) {
      toast.error("Eroare", { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-zinc-100" title={family.name}>{family.name}</h3>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {(family.skus || []).map((s) => (
              <span key={s} className="rounded bg-zinc-800 px-1 py-0.5 text-[9px] font-mono text-zinc-300">{s}</span>
            ))}
            <FamilyEmagLink pnks={family.pnks} testid={`emag-question-family-${family.family_id}-link`} />
          </div>
        </div>
        <span className="shrink-0 rounded-full border border-blue-500/40 bg-blue-500/10 px-2 py-0.5 text-xs font-bold text-blue-200">
          {q.total_captured || 0}
        </span>
      </div>

      {q.new > 0 && (
        <div className="mt-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-1 text-[11px] text-orange-200">
          <strong className="font-bold">{q.new}</strong> întrebări noi (după cutoff)
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-1.5">
        <button
          onClick={onExtract}
          disabled={extracting}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] font-medium text-zinc-100 hover:border-orange-500/40 disabled:opacity-50">
          <RefreshCw className={`h-3 w-3 ${extracting ? "animate-spin" : ""}`} />
          {extracting ? "Extrag..." : "Extrage întrebări"}
        </button>
        <button
          onClick={toggleExpand}
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] font-medium text-zinc-100 hover:border-orange-500/40">
          {expanded ? "Ascunde" : "Vezi lista"}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {busy && <p className="text-xs text-zinc-500">Încărcare...</p>}
          {!busy && items && items.length === 0 && (
            <p className="text-xs text-zinc-500">Nicio întrebare capturată încă.</p>
          )}
          {!busy && items && items.map((qi) => (
            <div key={qi.id} className="rounded border border-zinc-800 bg-zinc-950/60 p-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-zinc-200">{qi.user_name || "—"}</span>
                <span className="text-[10px] text-zinc-500">{fmtDateTime(qi.created_at_emag)}</span>
              </div>
              {qi.content && <div className="mt-1 text-zinc-300 whitespace-pre-wrap">{qi.content}</div>}
              <div className="mt-2 flex items-center gap-2">
                {qi.question_url && (
                  <a href={`https://www.emag.ro${qi.question_url}`} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] text-zinc-400 hover:text-orange-300">
                    <ExternalLink className="h-3 w-3" /> pe eMAG
                  </a>
                )}
                <span className="ml-auto">
                  {qi.gomag_sent ? (
                    <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300">Gomag ✓</span>
                  ) : (
                    <button
                      data-testid={`emag-question-gomag-${qi.id}`}
                      onClick={() => markGomag(qi.id)}
                      className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-500/20">
                      Trimite Gomag
                    </button>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Gomag Reviews (Răspuns Recenzii Gomag) ────────────────────────────

function GomagFamilyCard({ family, bucket, onReply }) {
  const total = bucket === "positive" ? family.positive : family.negative;
  const unanswered = bucket === "positive" ? family.unanswered_positive : family.unanswered_negative;
  const answered = total - unanswered;
  const isPos = bucket === "positive";

  const canReply = unanswered > 0;

  return (
    <div data-testid={`gomag-family-card-${family.family_id}`}
         className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4 flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-100 truncate" title={family.family_name}>
          {family.family_name}
        </h3>
        <div className="text-[11px] text-zinc-500 mt-0.5">
          {(family.skus || []).slice(0, 3).join(" · ")}
          {(family.skus || []).length > 3 && <span className="text-zinc-600"> +{family.skus.length - 3}</span>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-zinc-950/60 border border-zinc-800 px-2 py-1.5">
          <div className="text-[10px] text-zinc-500">Total</div>
          <div className="text-base font-bold text-zinc-100">{family.total}</div>
        </div>
        <div className={`rounded-md border px-2 py-1.5 ${isPos ? "bg-emerald-500/10 border-emerald-500/30" : "bg-rose-500/10 border-rose-500/30"}`}>
          <div className={`text-[10px] ${isPos ? "text-emerald-300/70" : "text-rose-300/70"}`}>
            {isPos ? "Pozitive" : "Negative"}
          </div>
          <div className={`text-base font-bold ${isPos ? "text-emerald-100" : "text-rose-100"}`}>{total}</div>
        </div>
        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-2 py-1.5">
          <div className="text-[10px] text-amber-300/70">Fără răspuns</div>
          <div className="text-base font-bold text-amber-100">{unanswered}</div>
        </div>
      </div>

      <div className="text-[11px] text-zinc-500">
        Răspunsuri postate: <span className="text-zinc-300">{answered}</span> din {total}
      </div>

      <button
        data-testid={`gomag-family-reply-${family.family_id}`}
        onClick={onReply}
        disabled={!canReply}
        className={`w-full rounded-md px-3 py-2 text-sm font-medium transition ${
          canReply
            ? isPos
              ? "bg-emerald-500 text-white hover:bg-emerald-400"
              : "bg-rose-500 text-white hover:bg-rose-400"
            : "bg-zinc-800 text-zinc-500 cursor-not-allowed"
        }`}
      >
        {canReply ? `Răspunde la ${unanswered} recenzii` : "Toate au răspuns"}
      </button>
    </div>
  );
}

function GomagReplyModal({ family, bucket, defaultText, onClose, onSubmitted }) {
  // localStorage key is bucket-scoped: the same user often types very
  // different replies for positive vs. negative buckets on the same
  // family, and we don't want to cross-contaminate them.
  const LS_KEY = `gomag-reply-draft-${bucket}`;
  const [text, setText] = useState(() => {
    // Priority: (1) explicit defaultText (predefined positive), then
    // (2) whatever the user was typing last time before refreshing.
    if (defaultText && defaultText.trim()) return defaultText;
    try { return localStorage.getItem(LS_KEY) || ""; } catch { return ""; }
  });
  const [samples, setSamples] = useState([]);
  const [loadingSamples, setLoadingSamples] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [submittedJobId, setSubmittedJobId] = useState(null);

  const unanswered = bucket === "positive" ? family.unanswered_positive : family.unanswered_negative;
  const isPos = bucket === "positive";

  // Custom limit — user types how many replies they want to send in
  // this batch. Defaults to the full unanswered count so a lazy click
  // on the primary button does the obvious thing.
  const [customLimit, setCustomLimit] = useState(Math.min(unanswered, 10));

  // Debounced auto-save to localStorage on every keystroke — this is
  // what makes the text SURVIVE a full page refresh (the user asked
  // for exactly this behaviour).
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (text) localStorage.setItem(LS_KEY, text);
        else localStorage.removeItem(LS_KEY);
      } catch { /* quota exceeded / privacy mode — silently ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [text, LS_KEY]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/gomag-reviews/family/${family.family_id}`, {
          params: { bucket },
          withCredentials: true,
        });
        if (alive) setSamples(r.data?.items || []);
      } catch { /* modal shows empty list if fetch fails */ }
      if (alive) setLoadingSamples(false);
    })();
    return () => { alive = false; };
  }, [family.family_id, bucket]);

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const r = await axios.post(`${API}/gomag-reviews/ai-suggest`,
        { family_id: family.family_id, bucket },
        { withCredentials: true });
      setText(r.data?.text || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI nu a putut genera un răspuns.");
    } finally {
      setSuggesting(false);
    }
  };

  // Both buttons hit the same endpoint; the "custom" one passes `limit`.
  const send = async (limit) => {
    // Guard against double-submission: once we've dispatched a job in
    // THIS modal instance, subsequent clicks are silently ignored so
    // the operator doesn't accidentally queue the same batch twice.
    if (sending || submittedJobId) return;
    setSending(true);
    try {
      const payload = { family_id: family.family_id, bucket, text: text.trim() };
      if (limit != null) payload.limit = Math.max(1, Math.min(unanswered, parseInt(limit) || 0));
      const r = await axios.post(`${API}/gomag-reviews/reply-family`, payload,
        { withCredentials: true });
      const jobId = r.data?.job_id;
      setSubmittedJobId(jobId);
      // Clear the draft — the text has been "used", and we don't want
      // it lingering across refreshes for the next unrelated batch.
      try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
      toast.success(`Job pornit pentru ${r.data?.total} recenzii. Rate-limit ~1/sec.`);
      // Open the progress view in a NEW TAB. The Reviews page reads the
      // `?job=<id>` query and auto-starts polling for that job so the
      // user can monitor it there and keep working in the original tab.
      try {
        window.open(`/reviews?tab=gomag&job=${jobId}`, "_blank", "noopener");
      } catch { /* pop-up blocked — banner still shows in original tab */ }
      // Notify the parent (Reviews page) so the banner appears in the
      // original tab as well, and the modal closes.
      onSubmitted(jobId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trimiterea a eșuat.");
    } finally {
      setSending(false);
    }
  };

  const disabled = sending || !!submittedJobId || text.trim().length < 5;

  return (
    <div data-testid="gomag-reply-modal"
         className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-3xl rounded-2xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg font-bold text-zinc-100">
              Răspunde: {family.family_name}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Bucket: <span className={isPos ? "text-emerald-300" : "text-rose-300"}>{isPos ? "Pozitive (4-5⭐)" : "Negative (1-2-3⭐)"}</span>
              {" · "}
              <span className="text-amber-300">{unanswered} recenzii fără răspuns</span>
            </p>
          </div>
          <button data-testid="gomag-reply-close" onClick={onClose}
                  className="text-zinc-500 hover:text-zinc-200">✕</button>
        </div>

        {/* Sample reviews */}
        <div className="mb-4">
          <div className="text-xs font-medium text-zinc-400 mb-1.5">Recenzii fără răspuns (primele 200):</div>
          <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 max-h-60 overflow-y-auto divide-y divide-zinc-800">
            {loadingSamples ? (
              <div className="p-3 text-xs text-zinc-500">Se încarcă…</div>
            ) : samples.length === 0 ? (
              <div className="p-3 text-xs text-zinc-500">Nicio recenzie găsită.</div>
            ) : samples.map((r) => (
              <div key={r.id} className="p-2.5 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-amber-300">{"★".repeat(r.rating)}<span className="text-zinc-700">{"★".repeat(5 - r.rating)}</span></span>
                  <span className="text-zinc-200 font-medium">{r.author}</span>
                  <span className="text-zinc-600">·</span>
                  <span className="text-zinc-500 truncate">{r.title}</span>
                </div>
                <div className="text-zinc-400 mt-0.5 line-clamp-2">{r.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Reply text */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-zinc-300">Textul răspunsului (va fi postat la fiecare recenzie de mai sus):</label>
            {!isPos && (
              <button
                data-testid="gomag-reply-ai"
                onClick={aiSuggest}
                disabled={suggesting || !!submittedJobId}
                className="rounded-md bg-purple-500/20 border border-purple-500/40 px-2.5 py-1 text-xs font-medium text-purple-100 hover:bg-purple-500/30 disabled:opacity-50"
              >
                {suggesting ? "Se generează..." : "✨ Generează cu AI"}
              </button>
            )}
          </div>
          <textarea
            data-testid="gomag-reply-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            placeholder={isPos ? "Casuta goala. Poti completa cu textul tau, sau salveaza mai sus un text predefinit." : "Casuta goala. Completeaza manual sau apasa Generează cu AI."}
            disabled={!!submittedJobId}
            className="w-full rounded-md border border-zinc-700 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-orange-500/60 disabled:opacity-60"
          />
          <div className="mt-1 flex items-center justify-between text-[11px] text-zinc-500">
            <span>{text.length} caractere</span>
            <span className="italic">Textul se salvează automat pe măsură ce scrii. Rezistă la refresh.</span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 space-y-3">
          {/* Custom-limit send row */}
          <div className="flex items-center gap-2 flex-wrap rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
            <label className="text-xs text-zinc-400">Trimite doar la</label>
            <input
              data-testid="gomag-reply-custom-limit"
              type="number"
              value={customLimit}
              onChange={(e) => setCustomLimit(e.target.value)}
              min="1"
              max={unanswered}
              disabled={!!submittedJobId}
              className="w-24 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 outline-none focus:border-orange-500/60 disabled:opacity-60"
            />
            <label className="text-xs text-zinc-400">recenzii (din {unanswered} fără răspuns)</label>
            <div className="flex-1" />
            <button
              data-testid="gomag-reply-send-custom"
              onClick={() => send(customLimit)}
              disabled={disabled}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition disabled:opacity-50 ${
                isPos ? "bg-emerald-500 hover:bg-emerald-400" : "bg-rose-500 hover:bg-rose-400"
              }`}
            >
              Răspunde la {customLimit || 0} recenzii
            </button>
          </div>

          {/* Reply-to-all + cancel */}
          <div className="flex items-center justify-between gap-2">
            <button data-testid="gomag-reply-cancel" onClick={onClose} disabled={sending}
                    className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700 disabled:opacity-50">
              Anulează
            </button>
            <button
              data-testid="gomag-reply-send"
              onClick={() => send(null)}
              disabled={disabled}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition disabled:opacity-50 ${
                isPos ? "bg-emerald-500 hover:bg-emerald-400" : "bg-rose-500 hover:bg-rose-400"
              }`}
            >
              {sending ? "Pornește..." : submittedJobId ? "✓ Trimis" : `Răspunde la toate cele ${unanswered} recenzii`}
            </button>
          </div>

          {submittedJobId && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
              Job pornit. S-a deschis un tab nou cu progresul. Închide această fereastră când vrei.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Customer Reviews (Recenzii Clienți Gomag) ───────────────────────
function BragusReviewsTab() {
  // ── Wrapper tab „Recenzii BRAGUS.ro" cu 2 sub-tab-uri (user cerinta Feb 2026):
  // - „Recenzii din eMAG"  → import automat spre Judge.me (Shopify). WIP.
  // - „Recenzii din site"  → recenziile primite direct de la clienți (fluxul
  //   existent gestionat prin `CustomerReviewsTab`).
  const [sub, setSub] = useState("emag");
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-zinc-800/60">
        <button
          data-testid="bragus-sub-emag"
          onClick={() => setSub("emag")}
          className={`relative px-3 py-1.5 text-xs font-semibold transition ${
            sub === "emag" ? "text-amber-300" : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii din eMAG
          {sub === "emag" && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-amber-400" />}
        </button>
        <button
          data-testid="bragus-sub-seller-emag"
          onClick={() => setSub("site")}
          className={`relative px-3 py-1.5 text-xs font-semibold transition ${
            sub === "site" ? "text-amber-300" : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Recenzii Seller eMAG
          {sub === "site" && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-amber-400" />}
        </button>
      </div>
      {sub === "emag" ? <BragusEmagReviewsTab /> : <EmagSellerReviewsTab />}
    </div>
  );
}

// ─── Recenzii Seller eMAG (import RPA din pagina publica eMAG) ─────
// User cerinta Feb 2026: tabelul afiseaza recenziile scraped (colectie
// `emag_seller_reviews`) cu buton individual + bulk pt trimitere la Judge.me
// ca SHOP review (nu produs), plus posibilitatea de marcare manuala.
function EmagSellerReviewsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busyIds, setBusyIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [totals, setTotals] = useState({ total: 0, sent: 0, unsent: 0 });
  const [filter, setFilter] = useState("all"); // all | sent | unsent

  const load = useCallback(async (nextFilter) => {
    setLoading(true);
    try {
      const f = nextFilter || filter;
      const r = await axios.get(`${API}/emag/seller-reviews`, {
        params: { filter: f },
        withCredentials: true,
      });
      setItems(r.data?.items || []);
      setTotals({
        total: r.data?.total || 0,
        sent: r.data?.sent || 0,
        unsent: r.data?.unsent || 0,
      });
    } catch (e) {
      toast.error("Nu am putut încărca recenziile seller eMAG.", {
        description: e.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const changeFilter = (next) => {
    if (next === filter) return;
    setFilter(next);
    setSelected(new Set()); // reset selectii cand schimbam filtru
    load(next);
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    // Skip "Nu trimite" (do_not_send) și cele deja trimise.
    const eligible = items.filter((it) => !it.sent_to_shopify && !it.do_not_send).map((it) => it.id);
    if (selected.size >= eligible.length && eligible.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligible));
    }
  };

  const setDoNotSend = async (id, val) => {
    const label = val ? 'Nu trimite (NU rosu)' : 'revenire (poate fi trimisa din nou)';
    if (!window.confirm(`Marchezi această recenzie ca ${label}?`)) return;
    setBusyIds((prev) => new Set([...prev, id]));
    try {
      await axios.patch(
        `${API}/emag/seller-reviews/${encodeURIComponent(id)}/do-not-send`,
        { do_not_send: val },
        { withCredentials: true },
      );
      toast.success(val ? "Marcată ca Nu trimite" : "Revenire aplicată");
      // Scoate din selectie daca era selectata
      setSelected((prev) => { const next = new Set(prev); next.delete(id); return next; });
      await load();
    } catch (e) {
      toast.error("Marcare eșuată.", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
    }
  };

  const sendReviews = async (ids) => {
    if (!ids || ids.length === 0) return;
    if (ids.length > 1 && !window.confirm(`Trimit ${ids.length} recenzii la Judge.me ca recenzii magazin?`)) return;
    const idSet = new Set(ids);
    setBusyIds((prev) => new Set([...prev, ...ids]));
    if (ids.length > 1) setBulkBusy(true);
    try {
      const r = await axios.post(
        `${API}/emag/seller-reviews/send`,
        { review_ids: ids },
        { withCredentials: true, timeout: 120000 },
      );
      const d = r.data || {};
      const ok = d.ok || 0;
      const failed = d.failed || 0;
      if (ok > 0 && failed === 0) toast.success(`${ok} recenzii trimise la Judge.me`);
      else if (ok > 0 && failed > 0) toast.warning(`${ok} trimise, ${failed} eșuate`);
      else toast.error("Nicio recenzie trimisă.", { description: JSON.stringify(d.results?.[0]?.response || {}).slice(0, 200) });
      setSelected((prev) => {
        const next = new Set(prev);
        for (const rid of ids) next.delete(rid);
        return next;
      });
      await load();
    } catch (e) {
      toast.error("Trimitere eșuată.", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        for (const rid of ids) next.delete(rid);
        return next;
      });
      setBulkBusy(false);
    }
  };

  const markManually = async (id, sent) => {
    const label = sent ? "trimisă" : "netrimisă";
    if (!window.confirm(`Marchezi manual această recenzie ca ${label}? (nu se face POST către Judge.me)`)) return;
    setBusyIds((prev) => new Set([...prev, id]));
    try {
      await axios.patch(
        `${API}/emag/seller-reviews/${encodeURIComponent(id)}/mark`,
        { sent },
        { withCredentials: true },
      );
      toast.success(`Recenzie marcată ca ${label}`);
      await load();
    } catch (e) {
      toast.error("Marcare eșuată.", { description: e.response?.data?.detail || e.message });
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const eligibleForBulk = items.filter((it) => !it.sent_to_shopify && !it.do_not_send).map((it) => it.id);
  const allEligibleSelected = eligibleForBulk.length > 0 && eligibleForBulk.every((id) => selected.has(id));

  return (
    <div className="space-y-4" data-testid="emag-seller-reviews-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Recenzii Seller eMAG</h2>
          <p className="mt-1 text-xs text-zinc-500">
            Recenzii extrase de pe pagina publică eMAG (Reviewuri vânzător). Le trimiți la Judge.me ca <b>recenzii magazin</b> (nu recenzii produs).
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <button
            data-testid="emag-seller-filter-all"
            onClick={() => changeFilter("all")}
            className={`rounded-md border px-2 py-1 transition ${
              filter === "all"
                ? "border-zinc-500 bg-zinc-700/60 text-zinc-100 ring-1 ring-zinc-400"
                : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:text-zinc-100"
            }`}
          >
            Total: <span className="font-mono">{totals.total}</span>
          </button>
          <button
            data-testid="emag-seller-filter-sent"
            onClick={() => changeFilter("sent")}
            className={`rounded-md border px-2 py-1 transition ${
              filter === "sent"
                ? "border-emerald-400 bg-emerald-500/25 text-emerald-100 ring-1 ring-emerald-300"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:text-emerald-100"
            }`}
          >
            Trimise: <span className="font-mono">{totals.sent}</span>
          </button>
          <button
            data-testid="emag-seller-filter-unsent"
            onClick={() => changeFilter("unsent")}
            className={`rounded-md border px-2 py-1 transition ${
              filter === "unsent"
                ? "border-amber-400 bg-amber-500/25 text-amber-100 ring-1 ring-amber-300"
                : "border-amber-500/30 bg-amber-500/10 text-amber-300 hover:text-amber-100"
            }`}
          >
            Netrimise: <span className="font-mono">{totals.unsent}</span>
          </button>
          <button
            data-testid="emag-seller-reviews-refresh"
            onClick={() => load()}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Reîncarcă
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div
          data-testid="emag-seller-reviews-bulk-bar"
          className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2"
        >
          <span className="text-xs font-medium text-amber-200">
            {selected.size} {selected.size === 1 ? "recenzie selectată" : "recenzii selectate"}
          </span>
          <button
            data-testid="emag-seller-reviews-send-bulk"
            onClick={() => sendReviews(Array.from(selected))}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-3 py-1 text-xs font-semibold text-zinc-900 hover:bg-amber-400 disabled:opacity-50"
          >
            {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
            Trimite selectate la Judge.me
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="text-xs text-zinc-400 hover:text-zinc-200"
          >
            Deselectează
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500">Se încarcă recenziile…</p>
      ) : items.length === 0 ? (
        <p className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-6 text-center text-sm text-zinc-500">
          Nu există recenzii importate. Rulează scriptul <code>/app/scripts/emag_vendor_reviews_scrape.py</code> ca să extragi recenziile de pe eMAG.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/70 text-[11px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left">
                  <input
                    type="checkbox"
                    data-testid="emag-seller-reviews-select-all"
                    checked={allEligibleSelected}
                    onChange={toggleAll}
                    disabled={eligibleForBulk.length === 0}
                    className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500"
                  />
                </th>
                <th className="px-3 py-2 text-left">Nume</th>
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Data trimiterii</th>
                <th className="px-3 py-2 text-left">Rating</th>
                <th className="px-3 py-2 text-left">Text</th>
                <th className="px-3 py-2 text-center">Trimis Shopify</th>
                <th className="px-3 py-2 text-center">Acțiune</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 text-zinc-200">
              {items.map((it) => {
                const isSent = it.sent_to_shopify;
                const isDoNot = it.do_not_send;
                const isBusy = busyIds.has(it.id);
                return (
                  <tr key={it.id} data-testid={`emag-seller-review-row-${it.id}`} className="hover:bg-zinc-900/40">
                    <td className="px-2 py-2 align-top">
                      <input
                        type="checkbox"
                        data-testid={`emag-seller-review-select-${it.id}`}
                        checked={selected.has(it.id)}
                        onChange={() => toggleOne(it.id)}
                        disabled={isSent || isDoNot}
                        className="h-3.5 w-3.5 rounded border-zinc-600 bg-zinc-800 text-amber-500 focus:ring-amber-500 disabled:opacity-40"
                      />
                    </td>
                    <td className="px-3 py-2 align-top font-medium text-zinc-100">{it.author}</td>
                    <td className="px-3 py-2 align-top text-xs text-zinc-400">{it.date}</td>
                    <td className="px-3 py-2 align-top text-xs text-zinc-400 whitespace-nowrap" data-testid={`emag-seller-review-sent-at-${it.id}`}>
                      {it.sent_at ? new Date(it.sent_at).toLocaleString("ro-RO", { timeZone: "Europe/Bucharest", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <Star
                            key={s}
                            className={`h-3.5 w-3.5 ${s <= it.rating ? "fill-amber-400 text-amber-400" : "text-zinc-700"}`}
                          />
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-xs text-zinc-300 max-w-[520px]">
                      <div className="line-clamp-3 whitespace-pre-wrap" title={it.text}>{it.text}</div>
                    </td>
                    <td className="px-3 py-2 align-top text-center">
                      {isSent ? (
                        <span
                          data-testid={`emag-seller-review-badge-sent-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300 ring-1 ring-emerald-500/40"
                        >
                          <CheckCircle2 className="h-3 w-3" /> Da{it.manually_marked ? " (manual)" : ""}
                        </span>
                      ) : isDoNot ? (
                        <span
                          data-testid={`emag-seller-review-badge-donotsend-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-300 ring-1 ring-rose-500/50"
                          title="Marcată ca Nu trimite — nu se poate push la Judge.me"
                        >
                          <AlertCircle className="h-3 w-3" /> NU
                        </span>
                      ) : (
                        <span
                          data-testid={`emag-seller-review-badge-unsent-${it.id}`}
                          className="inline-flex items-center gap-1 rounded-full bg-zinc-800 px-2 py-0.5 text-[11px] font-semibold text-zinc-400 ring-1 ring-zinc-700"
                        >
                          Nu
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top text-center">
                      {isSent ? (
                        <button
                          data-testid={`emag-seller-review-mark-unsent-${it.id}`}
                          onClick={() => markManually(it.id, false)}
                          disabled={isBusy}
                          title="Marchează ca netrimisă (revenire manuală)"
                          className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-400 hover:text-zinc-100 disabled:opacity-50"
                        >
                          Marchează netrimisă
                        </button>
                      ) : isDoNot ? (
                        <button
                          data-testid={`emag-seller-review-undo-donotsend-${it.id}`}
                          onClick={() => setDoNotSend(it.id, false)}
                          disabled={isBusy}
                          title="Anulează Nu trimite (recenzia devine trimisibilă)"
                          className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] text-rose-200 hover:text-rose-100 disabled:opacity-50"
                        >
                          Anulează Nu trimite
                        </button>
                      ) : (
                        <div className="flex flex-wrap items-center justify-center gap-1">
                          <button
                            data-testid={`emag-seller-review-send-${it.id}`}
                            onClick={() => sendReviews([it.id])}
                            disabled={isBusy}
                            className="inline-flex items-center gap-1 rounded-md bg-amber-500 px-2 py-1 text-[11px] font-semibold text-zinc-900 hover:bg-amber-400 disabled:opacity-50"
                          >
                            {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
                            Trimite
                          </button>
                          <button
                            data-testid={`emag-seller-review-mark-sent-${it.id}`}
                            onClick={() => markManually(it.id, true)}
                            disabled={isBusy}
                            title="Marchează ca trimisă (fără POST către Judge.me)"
                            className="inline-flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 hover:text-zinc-100 disabled:opacity-50"
                          >
                            Marchează trimisă
                          </button>
                          <button
                            data-testid={`emag-seller-review-donotsend-${it.id}`}
                            onClick={() => setDoNotSend(it.id, true)}
                            disabled={isBusy}
                            title="Nu trimite — recenzia va apărea cu NU roșu, exclusă de la Judge.me"
                            className="inline-flex items-center gap-1 rounded-md border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[11px] font-semibold text-rose-300 hover:text-rose-100 disabled:opacity-50"
                          >
                            Nu trimite
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function BragusEmagReviewsTab() {
  // Feb 2026 iteratia 2 — per-SKU view (nu mai grupam pe familii). Fiecare
  // SKU are card propriu cu titlu editabil, chip-ul SKU (link direct Bragus.ro),
  // 3 bare per-stea: eMAG / Trimise Bragus.ro / Raspunse Bragus.ro.
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTitleFor, setSavingTitleFor] = useState(null);
  const [shopDomain, setShopDomain] = useState("");
  const [search, setSearch] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [excludingFamilyFor, setExcludingFamilyFor] = useState(null);
  const [extractingFor, setExtractingFor] = useState(null);
  const [replyingFor, setReplyingFor] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludedFamilies, setExcludedFamilies] = useState([]);
  const autoScanTriedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews/bragus-emag-skus`, { withCredentials: true });
      setItems(r.data?.items || []);
      setShopDomain(r.data?.shop_domain || "");
    } catch (e) {
      toast.error("Nu am putut încărca SKU-urile.", { description: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadExcluded = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reviews/bragus-emag-skus/excluded`, { withCredentials: true });
      setExcludedFamilies(r.data?.items || []);
    } catch (e) {
      toast.error("Nu am putut încărca familiile excluse.", { description: e.response?.data?.detail || e.message });
    }
  }, []);

  const runScan = useCallback(async (onlyMissing = false) => {
    setScanning(true);
    setScanProgress({ msg: onlyMissing ? "Se scanează SKU-urile lipsă…" : "Se scanează toate SKU-urile…" });
    try {
      const r = await axios.post(
        `${API}/reviews/bragus-emag-skus/scan-emag`,
        { only_missing: onlyMissing },
        { withCredentials: true, timeout: 15 * 60 * 1000 },
      );
      const d = r.data || {};
      setScanProgress({ msg: `Scanare terminată: ${d.ok || 0}/${d.scanned || 0} PNK-uri OK` });
      if ((d.failed || []).length > 0) {
        toast.error(`Scanare cu erori: ${d.failed.length} PNK-uri au eșuat`);
      } else if (d.scanned > 0) {
        toast.success(`Scanare terminată — ${d.ok} PNK-uri actualizate`);
      } else {
        toast.info("Toate SKU-urile aveau deja recenzii scanate.");
      }
      await load();
    } catch (e) {
      toast.error("Scanare eșuată.", { description: e.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
      setTimeout(() => setScanProgress(null), 4000);
    }
  }, [load]);

  // Auto-scan (only_missing) când > 30% din SKU-uri au 0 recenzii (probabil nu au fost scanate încă)
  useEffect(() => {
    if (autoScanTriedRef.current || loading || scanning) return;
    if (items.length === 0) return;
    const missing = items.filter((it) => !it.emag_last_scan_at).length;
    if (missing > 0 && missing / items.length > 0.3) {
      autoScanTriedRef.current = true;
      runScan(true);
    }
  }, [items, loading, scanning, runScan]);

  const updateTitle = async (sku, newTitle) => {
    setSavingTitleFor(sku);
    try {
      const r = await axios.patch(
        `${API}/reviews/bragus-emag-skus/${encodeURIComponent(sku)}/title`,
        { title: newTitle },
        { withCredentials: true },
      );
      setItems((prev) => prev.map((it) => it.sku === sku
        ? { ...it, custom_title: r.data.custom_title, title: r.data.custom_title || it.default_title }
        : it));
      toast.success("Titlu salvat");
    } catch (e) {
      toast.error("Salvare eșuată.", { description: e.response?.data?.detail || e.message });
    } finally { setSavingTitleFor(null); }
  };

  const excludeFamily = async (item) => {
    const familyLabel = item.family_name ? `familia „${item.family_name}"` : `SKU-ul ${item.sku}`;
    if (!window.confirm(`Sigur excluzi ${familyLabel} din pagina Bragus.ro?\n\n` +
      `SKU-urile familiei nu se vor mai afișa aici și NU vor fi trimise automat/manual la Judge.me. ` +
      `Familia rămâne în modulul CRM „Apeluri Recenzii". Poți restaura oricând din „Familii excluse".`)) {
      return;
    }
    setExcludingFamilyFor(item.sku);
    try {
      const r = await axios.post(
        `${API}/reviews/bragus-emag-skus/${encodeURIComponent(item.sku)}/exclude-family`,
        {},
        { withCredentials: true },
      );
      toast.success(`${r.data.count || 1} SKU-uri excluse din Bragus.ro`,
        { description: r.data.family_name ? `Familia: ${r.data.family_name}` : `SKU: ${item.sku}` });
      await load();
      if (showExcluded) await loadExcluded();
    } catch (e) {
      toast.error("Excludere eșuată.", { description: e.response?.data?.detail || e.message });
    } finally { setExcludingFamilyFor(null); }
  };

  const undoExclude = async (fam) => {
    const anySku = fam.skus && fam.skus[0];
    if (!anySku) return;
    try {
      await axios.delete(
        `${API}/reviews/bragus-emag-skus/${encodeURIComponent(anySku)}/exclude-family`,
        { withCredentials: true },
      );
      toast.success(`Restaurat ${fam.family_name || anySku}`);
      await loadExcluded();
      await load();
    } catch (e) {
      toast.error("Restaurare eșuată.", { description: e.response?.data?.detail || e.message });
    }
  };

  const extractReviews = async (item) => {
    setExtractingFor(item.sku);
    try {
      // Launch background job
      const startResp = await axios.post(
        `${API}/reviews/bragus-emag-skus/${encodeURIComponent(item.sku)}/extract-reviews`,
        {},
        { withCredentials: true },
      );
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("Job ID lipsa in response");
      toast.info(`Extract pornit pentru ${item.sku}…`, {
        description: "Poate dura 2-5 minute (fetch complet eMAG per PNK)",
      });

      // Poll until done
      const pollStart = Date.now();
      const pollTimeoutMs = 15 * 60 * 1000; // 15 min hard cap
      let job = null;
      while (Date.now() - pollStart < pollTimeoutMs) {
        await new Promise((res) => setTimeout(res, 5000));
        try {
          const st = await axios.get(
            `${API}/reviews/bragus-emag-skus/extract-jobs/${jobId}`,
            { withCredentials: true },
          );
          job = st.data;
          if (job?.status === "done" || job?.status === "error") break;
        } catch (pollErr) {
          // ignore intermittent errors
        }
      }
      if (!job || job.status === "error") {
        throw new Error(job?.error || "Extract eșuat / timeout");
      }
      toast.success(`${job.extracted || 0} recenzii extrase pentru ${item.sku}`, {
        description: `Familie eMAG total: ${job.family_total_on_emag} · scanate: ${job.family_total_scanned}`,
      });

      // Trigger CSV download
      try {
        const csvResp = await axios.get(
          `${API}/reviews/bragus-emag-skus/${encodeURIComponent(item.sku)}/download-csv`,
          { withCredentials: true, responseType: "blob" },
        );
        const blob = new Blob([csvResp.data], { type: "text/csv;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `judgeme_import_${item.sku}.csv`;
        document.body.appendChild(link); link.click();
        setTimeout(() => { URL.revokeObjectURL(link.href); link.remove(); }, 1000);
      } catch (dlErr) {
        toast.error("Descărcare CSV eșuată.", { description: dlErr.response?.data?.detail || dlErr.message });
      }
      await load();
    } catch (e) {
      toast.error(`Extragere eșuată pentru ${item.sku}`,
        { description: e.response?.data?.detail || e.message });
    } finally { setExtractingFor(null); }
  };

  const generateReplies = async (item) => {
    setReplyingFor(item.sku);
    try {
      // Launch background reply job
      const startResp = await axios.post(
        `${API}/reviews/bragus-emag-skus/${encodeURIComponent(item.sku)}/generate-replies`,
        {},
        { withCredentials: true },
      );
      const jobId = startResp.data?.job_id;
      if (!jobId) throw new Error("Job ID lipsa");
      toast.info(`Se răspund recenziile ${item.sku}…`, {
        description: "Match pe title+body pe Judge.me · POST reply direct via API",
      });

      // Poll until done
      const pollStart = Date.now();
      const pollTimeoutMs = 20 * 60 * 1000; // 20 min hard cap
      let job = null;
      while (Date.now() - pollStart < pollTimeoutMs) {
        await new Promise((res) => setTimeout(res, 4000));
        try {
          const st = await axios.get(
            `${API}/reviews/bragus-emag-skus/reply-jobs/${jobId}`,
            { withCredentials: true },
          );
          job = st.data;
          if (job?.status === "done" || job?.status === "error") break;
        } catch (pollErr) {
          // ignore
        }
      }
      if (!job || job.status === "error") {
        throw new Error(job?.error || "Reply job esuat / timeout");
      }
      toast.success(`Reply-uri postate la Judge.me: ${job.posted_ok || 0}`, {
        description:
          `${job.generated || 0} generate · ${job.posted_ok || 0} OK · ` +
          `${job.posted_failed || 0} eșec · ${job.not_found_on_judgeme || 0} lipsa pe Judge.me (urca CSV întâi)`,
      });
      await load();
    } catch (e) {
      toast.error(`Reply eșuat pentru ${item.sku}`,
        { description: e.response?.data?.detail || e.message });
    } finally { setReplyingFor(null); }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return it.sku.toLowerCase().includes(q) || (it.title || "").toLowerCase().includes(q);
  });
  const totalEmag = filtered.reduce((s, it) => s + (it.emag_total_reviews || 0), 0);
  const totalSent = filtered.reduce((s, it) => s + Object.values(it.sent_to_bragus_distribution || {}).reduce((a, b) => a + Number(b || 0), 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-zinc-100">SKU-uri cu match în Shopify</h3>
          <p className="text-[11px] text-zinc-500">
            Câte 1 card per SKU. Click pe titlu pentru a-l redenumi; click pe chip-ul SKU deschide produsul pe Bragus.ro.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-zinc-400">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută SKU / titlu…"
            className="rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-200 outline-none focus:border-amber-500/40"
            data-testid="bragus-sku-search"
          />
          <span><b className="text-amber-300">{filtered.length}</b> SKU</span>
          <span>·</span>
          <span><b className="text-amber-200">{totalEmag}</b> eMAG</span>
          <span>·</span>
          <span><b className="text-emerald-300">{totalSent}</b> trimise</span>
          <button
            onClick={() => runScan(false)}
            disabled={scanning}
            data-testid="bragus-emag-scan-all"
            title="Scanează recenziile eMAG pentru toate SKU-urile (rescan complet)."
            className="ml-2 inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-60"
          >
            {scanning ? (<><Loader2 className="h-3 w-3 animate-spin" /> Se scanează…</>) : (<><RefreshCw className="h-3 w-3" /> Scanează eMAG</>)}
          </button>
          <button
            onClick={load}
            data-testid="bragus-emag-refresh"
            className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 hover:border-amber-500/40"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          <button
            onClick={() => { const nxt = !showExcluded; setShowExcluded(nxt); if (nxt) loadExcluded(); }}
            data-testid="bragus-emag-toggle-excluded"
            className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[11px] text-zinc-300 hover:border-amber-500/40"
          >
            {showExcluded ? "Ascunde excluse" : "Familii excluse"}
          </button>
        </div>
      </div>

      {scanProgress && (
        <div className="rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200" data-testid="bragus-emag-scan-progress">
          {scanProgress.msg}
        </div>
      )}

      {showExcluded && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid="bragus-emag-excluded-panel">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-xs font-semibold text-zinc-300">Familii excluse din Bragus.ro</h4>
            <span className="text-[11px] text-zinc-500">{excludedFamilies.length}</span>
          </div>
          {excludedFamilies.length === 0 ? (
            <p className="text-[11px] text-zinc-500">Nici o familie exclusă.</p>
          ) : (
            <ul className="space-y-1">
              {excludedFamilies.map((f, i) => (
                <li key={i} className="flex items-center justify-between gap-2 rounded bg-zinc-900/60 px-2 py-1 text-[11px] text-zinc-300">
                  <div className="min-w-0 flex-1">
                    <div className="truncate">{f.family_name || `Solo SKU: ${(f.skus || [])[0]}`}</div>
                    <div className="text-[10px] text-zinc-500">
                      {(f.skus || []).length} SKU · {(f.skus || []).slice(0, 6).join(", ")}{(f.skus || []).length > 6 ? "…" : ""}
                    </div>
                  </div>
                  <button
                    onClick={() => undoExclude(f)}
                    data-testid={`bragus-emag-undo-exclude-${(f.skus || [])[0]}`}
                    className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-200 hover:bg-emerald-500/20"
                  >
                    Restaurează
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {loading && <p className="text-sm text-zinc-500">Se încarcă SKU-urile Shopify…</p>}

      {!loading && filtered.length === 0 && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-6 text-center text-sm text-zinc-400">
          Nici un SKU matchat în Shopify.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {filtered.map((it) => (
            <BragusEmagSkuCard
              key={it.sku}
              item={it}
              saving={savingTitleFor === it.sku}
              excluding={excludingFamilyFor === it.sku}
              extracting={extractingFor === it.sku}
              replying={replyingFor === it.sku}
              onUpdateTitle={(t) => updateTitle(it.sku, t)}
              onExcludeFamily={() => excludeFamily(it)}
              onExtractReviews={() => extractReviews(it)}
              onGenerateReplies={() => generateReplies(it)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function BragusEmagSkuCard({ item: it, saving, excluding, extracting, replying, onUpdateTitle, onExcludeFamily, onExtractReviews, onGenerateReplies }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [draft, setDraft] = useState(it.title || "");
  useEffect(() => { setDraft(it.title || ""); }, [it.title]);
  const totalSent = Object.values(it.sent_to_bragus_distribution || {}).reduce((a, b) => a + Number(b || 0), 0);
  const totalReplied = Object.values(it.replied_on_bragus_distribution || {}).reduce((a, b) => a + Number(b || 0), 0);
  const localPending = Number(it.local_pending_replies || 0);
  // Feb 2026: afiseaza butonul si cand Judge.me raporteaza has_reply=true
  // pentru toate (din CSV initial), dar noi avem reply-uri locale netrimise
  // (ex. BRAG-68: 2 OK · 28 lipsa pe JM). `unposted` inseamna cate mai avem
  // efectiv de trimis la Judge.me din pipeline-ul nostru.
  const unposted = Math.max(totalSent - totalReplied, localPending);
  const hasUnanswered = unposted > 0;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4" data-testid={`bragus-emag-sku-${it.sku}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          {editingTitle ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { onUpdateTitle(draft); setEditingTitle(false); }
                  if (e.key === "Escape") { setDraft(it.title || ""); setEditingTitle(false); }
                }}
                className="w-full rounded border border-amber-500/40 bg-zinc-950 px-2 py-1 text-sm font-semibold text-zinc-100 outline-none"
              />
              <button
                onClick={() => { onUpdateTitle(draft); setEditingTitle(false); }}
                disabled={saving}
                className="rounded bg-amber-500 px-2 py-1 text-[10px] font-semibold text-black hover:bg-amber-400 disabled:opacity-60"
                title="Salvează"
              >OK</button>
              <button
                onClick={() => { setDraft(it.title || ""); setEditingTitle(false); }}
                className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:border-zinc-500"
              >×</button>
            </div>
          ) : (
            <button
              onClick={() => setEditingTitle(true)}
              className="text-left w-full"
              title="Click pentru a redenumi"
              data-testid={`bragus-sku-${it.sku}-title-btn`}
            >
              <h3 className="font-semibold text-zinc-100 truncate hover:text-amber-200 transition">{it.title}</h3>
              {it.custom_title && it.custom_title !== it.default_title && (
                <p className="text-[10px] text-zinc-500 truncate">Original: {it.default_title}</p>
              )}
            </button>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {it.shopify_handle ? (
              <a
                href={`https://bragus.ro/products/${it.shopify_handle}`}
                target="_blank"
                rel="noreferrer"
                title={`Deschide ${it.sku} pe Bragus.ro`}
                data-testid={`bragus-sku-link-${it.sku}`}
              >
                <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-mono text-zinc-300 hover:bg-zinc-700 hover:text-amber-200 transition">
                  {it.sku}
                </span>
              </a>
            ) : (
              <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-mono text-zinc-500" title="Fără handle Shopify">
                {it.sku}
              </span>
            )}
            {it.pnk && (
              <a
                href={`https://www.emag.ro/-/pd/${it.pnk}`}
                target="_blank"
                rel="noopener noreferrer"
                title={`Deschide produsul pe eMAG (PNK ${it.pnk})`}
                data-testid={`bragus-sku-emag-link-${it.sku}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-md border border-orange-500/40 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-medium text-orange-200 hover:bg-orange-500/20"
              >
                <ExternalLink className="h-2.5 w-2.5" />
                eMAG
              </a>
            )}
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-200">
          <Star className="h-3 w-3" />
          {it.emag_total_reviews || 0}
        </span>
      </div>

      {it.family_name && (
        <p className="mt-1 truncate text-[10px] text-zinc-500" title={it.family_name}>
          Familie: <span className="text-zinc-400">{it.family_name}</span>
        </p>
      )}

      <div className="mt-1.5 grid grid-cols-1 gap-0.5 rounded border border-zinc-800/70 bg-zinc-950/40 px-2 py-1 text-[10px] text-zinc-400">
        <div className="flex items-center gap-1 truncate" title={it.pnk}>
          <span className="w-16 shrink-0 uppercase tracking-wider text-zinc-500">eMAG PNK</span>
          <span className="truncate font-mono text-orange-200">{it.pnk || "—"}</span>
        </div>
        <div className="flex items-center gap-1 truncate" title={String(it.shopify_product_id || "")}>
          <span className="w-16 shrink-0 uppercase tracking-wider text-zinc-500">Shopify ID</span>
          {it.shopify_product_id ? (
            <a
              href={`https://admin.shopify.com/store/bragus/products/${it.shopify_product_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`bragus-sku-${it.sku}-shopify-id`}
              className="truncate font-mono text-emerald-200 hover:text-emerald-100 hover:underline"
            >
              {it.shopify_product_id}
            </a>
          ) : (
            <span className="font-mono text-zinc-600">—</span>
          )}
        </div>
        <div className="flex items-center gap-1 truncate" title={it.shopify_handle}>
          <span className="w-16 shrink-0 uppercase tracking-wider text-zinc-500">Handle</span>
          {it.shopify_handle ? (
            <a
              href={`https://bragus.ro/products/${it.shopify_handle}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`bragus-sku-${it.sku}-shopify-handle`}
              className="truncate font-mono text-sky-200 hover:text-sky-100 hover:underline"
            >
              {it.shopify_handle}
            </a>
          ) : (
            <span className="font-mono text-zinc-600">—</span>
          )}
        </div>
      </div>

      <StarBar
        label="Recenzii eMAG"
        total={it.emag_total_reviews || 0}
        dist={it.emag_rating_distribution || {}}
        positiveColor="emerald"
        negativeColor="red"
        borderColor="amber"
        testidPrefix={`bragus-sku-${it.sku}-emag`}
      />
      <StarBar
        label="Trimise la Bragus.ro"
        total={Object.values(it.sent_to_bragus_distribution || {}).reduce((a, b) => a + Number(b || 0), 0)}
        dist={it.sent_to_bragus_distribution || {}}
        positiveColor="emerald"
        negativeColor="red"
        borderColor="emerald"
        testidPrefix={`bragus-sku-${it.sku}-sent`}
      />
      <StarBar
        label="Răspunse la Bragus.ro"
        total={Object.values(it.replied_on_bragus_distribution || {}).reduce((a, b) => a + Number(b || 0), 0)}
        dist={it.replied_on_bragus_distribution || {}}
        positiveColor="sky"
        negativeColor="red"
        borderColor="sky"
        testidPrefix={`bragus-sku-${it.sku}-replied`}
      />

      <div className="mt-3 flex items-center justify-between gap-2 border-t border-zinc-800/60 pt-2 text-[10px] text-zinc-500">
        <span>
          {it.emag_last_scan_at ? `Scanat: ${new Date(it.emag_last_scan_at).toLocaleDateString("ro-RO")}` : "Nescanat"}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={onExtractReviews}
            disabled={extracting || replying}
            data-testid={`bragus-sku-${it.sku}-extract`}
            title={`Scanează COMPLET eMAG pentru ${it.pnk}, salvează recenziile variantei și descarcă CSV pentru upload manual la Judge.me`}
            className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold text-amber-200 hover:bg-amber-500/20 disabled:opacity-60"
          >
            {extracting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {extracting ? "Extrage…" : "Extrage recenzii"}
          </button>
          {hasUnanswered && (
            <button
              onClick={onGenerateReplies}
              disabled={replying || extracting}
              data-testid={`bragus-sku-${it.sku}-reply`}
              title={`Generează răspunsuri pentru cele ${unposted} recenzii fără răspuns (4-5★: text fix, 1-3★: AI). ${localPending > 0 ? `${localPending} deja generate local, retrimit la Judge.me.` : "Reply-urile intră în CSV, nu se trimit automat la Bragus.ro."}`}
              className="inline-flex items-center gap-1 rounded border border-sky-500/40 bg-sky-500/10 px-2 py-1 text-[10px] font-semibold text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
            >
              {replying ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
              {replying ? "Se răspunde…" : `Răspunde (${unposted})`}
            </button>
          )}
          <button
            onClick={onExcludeFamily}
            disabled={excluding}
            data-testid={`bragus-sku-${it.sku}-exclude-family`}
            title={it.family_name ? `Exclude familia „${it.family_name}" din Bragus.ro` : `Exclude SKU-ul ${it.sku} din Bragus.ro`}
            className="inline-flex items-center gap-1 rounded border border-red-500/30 bg-red-500/5 px-2 py-1 text-[10px] font-semibold text-red-300 hover:bg-red-500/15 disabled:opacity-60"
          >
            {excluding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
            Șterge familia
          </button>
        </div>
      </div>
    </div>
  );
}


function BragusEmagFamilyCard({ family: f, shopDomain, saving, onUpdateCounter }) {
  const dist = f.emag_rating_distribution || {};
  const sentDist = f.sent_to_bragus_distribution || {};
  const repliedDist = f.replied_on_bragus_distribution || {};
  const totalStars = [5, 4, 3, 2, 1].reduce((s, k) => s + Number(dist[String(k)] || 0), 0) || 1;
  // Map SKU → Shopify handle for direct-link chips
  const skuToHandle = (f.shopify_products || []).reduce((acc, sp) => {
    if (sp.sku) acc[sp.sku] = sp.handle || "";
    return acc;
  }, {});
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4" data-testid={`bragus-emag-family-${f.id}`}>
      {/* Header — family name + SKU chips (clickable to Bragus.ro product) */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold text-zinc-100 truncate" title={f.name}>{f.name}</h3>
          <div className="mt-1 flex flex-wrap gap-1">
            {(f.skus || []).map((s) => {
              const handle = skuToHandle[s];
              const inner = <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-[10px] font-mono text-zinc-300 hover:bg-zinc-700 hover:text-amber-200 transition">{s}</span>;
              return handle ? (
                <a
                  key={s}
                  href={`https://bragus.ro/products/${handle}`}
                  target="_blank"
                  rel="noreferrer"
                  title={`Deschide ${s} pe Bragus.ro`}
                  data-testid={`bragus-sku-link-${s}`}
                >
                  {inner}
                </a>
              ) : (
                <span key={s} title="Fără match Shopify">{inner}</span>
              );
            })}
          </div>
        </div>
        <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs font-bold text-amber-200">
          <Star className="h-3 w-3" />
          {f.emag_total_reviews || 0}
        </span>
      </div>

      {/* Bar 1 — RECENZII EMAG */}
      <StarBar
        label="Recenzii eMAG"
        total={f.emag_total_reviews || 0}
        dist={dist}
        positiveColor="emerald"
        negativeColor="red"
        borderColor="amber"
        testidPrefix={`bragus-family-${f.id}-emag`}
      />

      {/* Bar 2 — TRIMISE LA BRAGUS.RO */}
      <StarBar
        label="Trimise la Bragus.ro"
        total={Object.values(sentDist).reduce((a, b) => a + Number(b || 0), 0)}
        dist={sentDist}
        positiveColor="emerald"
        negativeColor="red"
        borderColor="emerald"
        testidPrefix={`bragus-family-${f.id}-sent`}
      />

      {/* Bar 3 — RASPUNSE LA BRAGUS.RO */}
      <StarBar
        label="Răspunse la Bragus.ro"
        total={Object.values(repliedDist).reduce((a, b) => a + Number(b || 0), 0)}
        dist={repliedDist}
        positiveColor="sky"
        negativeColor="red"
        borderColor="sky"
        testidPrefix={`bragus-family-${f.id}-replied`}
      />
    </div>
  );
}

function StarBar({ label, total, dist, positiveColor, negativeColor, borderColor, testidPrefix }) {
  // Reusable per-star breakdown bar. positiveColor = 4-5★ tiles; negativeColor = 1-3★.
  const borderMap = { amber: "border-amber-500/25 bg-amber-500/5",
                      emerald: "border-emerald-500/25 bg-emerald-500/5",
                      sky: "border-sky-500/25 bg-sky-500/5" };
  const headerMap = { amber: "text-amber-300/80", emerald: "text-emerald-300/80", sky: "text-sky-300/80" };
  const totalMap  = { amber: "text-amber-200",    emerald: "text-emerald-200",    sky: "text-sky-200" };
  const posBox = positiveColor === "emerald" ? "border-emerald-500/30 bg-emerald-500/5"
                : positiveColor === "sky"     ? "border-sky-500/30 bg-sky-500/5"
                :                                "border-emerald-500/30 bg-emerald-500/5";
  const negBox = "border-red-500/30 bg-red-500/5";
  const posText = positiveColor === "emerald" ? "text-emerald-300/80" : "text-sky-300/80";
  const negText = "text-red-300/80";
  const posVal  = positiveColor === "emerald" ? "text-emerald-200"    : "text-sky-200";
  const negVal  = "text-red-200";
  return (
    <div className={`mt-2 rounded-md border p-2 ${borderMap[borderColor] || borderMap.amber}`}>
      <div className="mb-1 flex items-center justify-between">
        <span className={`text-[10px] uppercase ${headerMap[borderColor] || headerMap.amber}`}>{label}</span>
        <span className={`font-mono text-xs font-bold ${totalMap[borderColor] || totalMap.amber}`}>{total}</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {[5, 4, 3, 2, 1].map((s) => {
          const n = Number(dist[String(s)] || 0);
          const isPos = s >= 4;
          return (
            <div key={s} title={`${s} stele: ${n}`}
              data-testid={`${testidPrefix}-star-${s}`}
              className={`rounded border px-1 py-0.5 text-center ${isPos ? posBox : negBox}`}>
              <div className={`text-[9px] ${isPos ? posText : negText}`}>{s}★</div>
              <div className={`font-mono text-[11px] font-bold ${isPos ? posVal : negVal}`}>{n}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CounterCell({ label, value, max, color, saving, onChange, testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value));
  useEffect(() => { setDraft(String(value)); }, [value]);
  const c = color === "emerald"
    ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-200"
    : "border-sky-500/30 bg-sky-500/5 text-sky-200";
  const commit = () => {
    const n = Math.max(0, Math.min(Number(draft) || 0, max || 0));
    setEditing(false);
    if (n !== value) onChange(n);
  };
  return (
    <div className={`rounded-md border px-1.5 py-1.5 text-center ${c}`} data-testid={testid}>
      <div className="text-[9px] uppercase opacity-80">{label}</div>
      {editing ? (
        <input
          autoFocus
          type="number"
          min="0"
          max={max || undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(String(value)); setEditing(false); } }}
          className="mx-auto mt-0.5 block w-full max-w-[64px] rounded bg-zinc-950 px-1 py-0.5 text-center font-mono text-sm font-bold outline-none focus:ring-1 focus:ring-current"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={saving}
          className="mt-0.5 block w-full font-mono text-sm font-bold hover:underline"
          title="Click pentru a edita manual"
        >
          {value} <span className="opacity-50 text-[10px]">/ {max || 0}</span>
        </button>
      )}
    </div>
  );
}


function CustomerReviewsTab() {
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | positive | negative | investigate
  const [config, setConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === "positive") params.bucket = "positive";
      else if (filter === "negative") params.bucket = "negative";
      else if (filter === "investigate") params.investigation = "open";
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/customer-reviews`, { params, withCredentials: true }),
        axios.get(`${API}/customer-reviews/config`, { withCredentials: true }),
      ]);
      setItems(r1.data?.items || []);
      setCounters(r1.data?.counters || {});
      setConfig(r2.data || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nu am putut încărca recenziile.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setTriggering(true);
    try {
      const r = await axios.post(`${API}/customer-reviews/trigger-scan`, {}, { withCredentials: true });
      toast.success(`Scan: ${r.data.sent} cereri trimise, ${r.data.refreshed} statusuri Sameday actualizate.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan eșuat.");
    } finally {
      setTriggering(false);
    }
  };

  const saveConfig = async (patch) => {
    setSavingCfg(true);
    try {
      await axios.put(`${API}/customer-reviews/config`, patch, { withCredentials: true });
      const r = await axios.get(`${API}/customer-reviews/config`, { withCredentials: true });
      setConfig(r.data);
      toast.success("Configurare salvată.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvare eșuată.");
    } finally {
      setSavingCfg(false);
    }
  };

  const setInvestigation = async (token, status) => {
    try {
      await axios.post(`${API}/customer-reviews/${token}/investigation`, { status }, { withCredentials: true });
      toast.success(status === "resolved" ? "Investigație rezolvată." : "Investigație redeschisă.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update eșuat.");
    }
  };

  const pushGomag = async (token) => {
    try {
      const r = await axios.post(`${API}/customer-reviews/${token}/push-gomag`, {}, { withCredentials: true });
      if (r.data.status === "sent") toast.success("Recenzie postată pe Gomag.");
      else toast.error(r.data.reason || "Push eșuat.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Push eșuat.");
    }
  };

  return (
    <div className="space-y-5">
      {/* Header + counters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">Recenzii Clienți Gomag</h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Cerere automată la {config?.days_after_delivery || 14} zile după livrarea comenzii. Cod cupon: <span className="text-amber-300 font-mono">{config?.coupon_code || "BRAGUS10"}</span>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="customer-reviews-config"
            onClick={() => setShowConfig(true)}
            className="rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-200 hover:bg-zinc-800"
          >
            ⚙ Configurare
          </button>
          <button
            data-testid="customer-reviews-scan"
            onClick={triggerScan}
            disabled={triggering}
            className="rounded-md bg-amber-500/20 border border-amber-500/40 px-3 py-1.5 text-xs font-medium text-amber-100 hover:bg-amber-500/30 disabled:opacity-50"
          >
            {triggering ? "Se scanează..." : "▶ Scanează comenzi acum"}
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatBox label="Total recenzii" value={counters.total || 0} color="zinc" />
        <StatBox label="Pozitive (4-5⭐)" value={counters.positive || 0} color="emerald" />
        <StatBox label="Negative (1-3⭐)" value={counters.negative || 0} color="rose" />
        <StatBox label="De investigat" value={counters.open_investigations || 0} color="amber" />
        <StatBox label="Cereri trimise" value={counters.requests_sent || 0} color="blue" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        {[
          { key: "all", label: "Toate" },
          { key: "positive", label: "Pozitive" },
          { key: "negative", label: "Negative" },
          { key: "investigate", label: "🔍 De investigat" },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`customer-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`rounded-md px-3 py-1 text-xs font-medium transition ${
              filter === f.key ? "bg-amber-500/20 text-amber-100 border border-amber-500/40" : "text-zinc-500 hover:text-zinc-300 border border-transparent"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-sm text-zinc-500 py-10 text-center">Se încarcă...</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-10 text-center">Nicio recenzie încă. Rulează un scan pentru a începe.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {items.map((r) => (
            <CustomerReviewCard
              key={r.token}
              review={r}
              onInvestigation={setInvestigation}
              onPushGomag={pushGomag}
            />
          ))}
        </div>
      )}

      {showConfig && (
        <CustomerReviewsConfigModal
          config={config}
          saving={savingCfg}
          onClose={() => setShowConfig(false)}
          onSave={saveConfig}
        />
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  const map = {
    zinc: "border-zinc-700 bg-zinc-900/40 text-zinc-100",
    emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
    rose: "border-rose-500/30 bg-rose-500/10 text-rose-100",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-100",
    blue: "border-blue-500/30 bg-blue-500/10 text-blue-100",
  };
  return (
    <div className={`rounded-lg border ${map[color]} px-3 py-2`}>
      <div className="text-[10px] opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

function CustomerReviewCard({ review, onInvestigation, onPushGomag }) {
  const isPos = review.bucket === "positive";
  const inv = review.investigation_status;
  const pushed = review.gomag_push?.status === "sent";
  return (
    <div data-testid={`customer-review-${review.token.slice(0, 8)}`}
         className={`rounded-xl border p-4 ${isPos ? "border-emerald-500/30 bg-emerald-500/5" : "border-rose-500/30 bg-rose-500/5"}`}>
      <div className="flex items-start gap-4">
        {review.product_image ? (
          <img src={review.product_image} alt="" className="h-16 w-16 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="h-16 w-16 rounded-lg bg-zinc-800 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-amber-400 text-sm">
              {"★".repeat(review.rating)}<span className="text-zinc-700">{"★".repeat(5 - review.rating)}</span>
            </span>
            <span className="text-sm font-semibold text-zinc-100">{review.customer_name}</span>
            <span className="text-xs text-zinc-500">· {fmtDateTime(review.submitted_at)}</span>
            <span className="text-xs text-zinc-500">· Comanda #{review.gomag_number}</span>
          </div>
          <div className="text-xs text-zinc-400 mb-1">{review.product_name}</div>
          {review.title && <div className="text-sm font-medium text-zinc-100 mt-1">{review.title}</div>}
          <div className="text-sm text-zinc-300 mt-1 whitespace-pre-wrap">{review.description}</div>

          <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px]">
            <StatusPill status={review.coupon_email?.status} label="Cupon email" />
            <StatusPill status={review.wa_thanks?.status} label="WA thanks" />
            {isPos && <StatusPill status={review.gomag_push?.status} label="Push Gomag" />}
            {!isPos && inv === "open" && (
              <span className="rounded-full px-2 py-0.5 bg-amber-500/20 text-amber-200 border border-amber-500/40">🔍 De investigat</span>
            )}
            {!isPos && inv === "resolved" && (
              <span className="rounded-full px-2 py-0.5 bg-zinc-700 text-zinc-300">✓ Rezolvat</span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {!isPos && inv === "open" && (
              <button
                data-testid={`resolve-${review.token.slice(0, 8)}`}
                onClick={() => onInvestigation(review.token, "resolved")}
                className="rounded-md bg-emerald-500/20 border border-emerald-500/40 px-2.5 py-1 text-xs text-emerald-100 hover:bg-emerald-500/30"
              >
                ✓ Marchează rezolvat
              </button>
            )}
            {!isPos && inv === "resolved" && (
              <button
                onClick={() => onInvestigation(review.token, "open")}
                className="rounded-md border border-zinc-700 bg-zinc-800 px-2.5 py-1 text-xs text-zinc-200 hover:bg-zinc-700"
              >
                Redeschide investigație
              </button>
            )}
            {isPos && !pushed && (
              <button
                data-testid={`push-gomag-${review.token.slice(0, 8)}`}
                onClick={() => onPushGomag(review.token)}
                className="rounded-md bg-blue-500/20 border border-blue-500/40 px-2.5 py-1 text-xs text-blue-100 hover:bg-blue-500/30"
              >
                Postează pe Gomag
              </button>
            )}
          </div>

          {review.investigation_note && (
            <div className="mt-2 text-xs text-zinc-400 border-l-2 border-zinc-700 pl-2 italic">
              Notă: {review.investigation_note}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status, label }) {
  const map = {
    sent: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
    pending: "bg-zinc-800 text-zinc-400 border-zinc-700",
    skipped: "bg-zinc-800 text-zinc-500 border-zinc-700",
    error: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  };
  const cls = map[status] || map.pending;
  return (
    <span className={`rounded-full px-2 py-0.5 border ${cls}`}>
      {label}: {status || "?"}
    </span>
  );
}

function CustomerReviewsConfigModal({ config, saving, onClose, onSave }) {
  const [local, setLocal] = useState(config || {});
  useEffect(() => { setLocal(config || {}); }, [config]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-zinc-100">Configurare Recenzii Clienți</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">✕</button>
        </div>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <Toggle label="Activ" checked={!!local.enabled} onChange={(v) => setLocal({ ...local, enabled: v })} />
          <NumberField label="Zile după livrare (Sameday)" value={local.days_after_delivery || 14} onChange={(v) => setLocal({ ...local, days_after_delivery: v })} />
          <TextField label="URL frontend (opțional - pentru linkul din email/WA)" value={local.frontend_url || ""} onChange={(v) => setLocal({ ...local, frontend_url: v })} placeholder="https://bragus-app.ro" />
          <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">Template-uri Email (ID din Șabloane Email):</div>
          <TextField label="Cerere recenzie" value={local.email_tpl_request || ""} onChange={(v) => setLocal({ ...local, email_tpl_request: v })} placeholder="tpl_review_request" />
          <TextField label="Cupon (mulțumire)" value={local.email_tpl_coupon || ""} onChange={(v) => setLocal({ ...local, email_tpl_coupon: v })} placeholder="tpl_review_coupon" />
          <div className="text-xs text-zinc-500 pt-2 border-t border-zinc-800">Template-uri WhatsApp (numele aprobat de Meta):</div>
          <TextField label="Cerere recenzie (cu header IMG + button URL)" value={local.wa_tpl_request || ""} onChange={(v) => setLocal({ ...local, wa_tpl_request: v })} placeholder="tpl_review_request_wa" />
          <TextField label="Limbă (ro/en_US)" value={local.wa_tpl_request_language || "ro"} onChange={(v) => setLocal({ ...local, wa_tpl_request_language: v })} />
          <TextField label="Notificare cupon email" value={local.wa_tpl_thanks || ""} onChange={(v) => setLocal({ ...local, wa_tpl_thanks: v })} placeholder="tpl_review_thanks_wa" />
          <TextField label="Limbă" value={local.wa_tpl_thanks_language || "ro"} onChange={(v) => setLocal({ ...local, wa_tpl_thanks_language: v })} />
        </div>
        <div className="mt-5 rounded-md bg-zinc-900 border border-zinc-800 p-3 text-[11px] text-zinc-400">
          <strong className="text-zinc-300">Variabile disponibile în template-uri:</strong>
          <br />
          Email: <code>{"{customer_name}"}</code>, <code>{"{product_name}"}</code>, <code>{"{product_image}"}</code>, <code>{"{review_link}"}</code>, <code>{"{coupon_code}"}</code>, <code>{"{order_id}"}</code>
          <br />
          WhatsApp cerere: {"{{1}}=customer_name, {{2}}=product_name"}, header IMG=poza produs, button URL suffix=token
          <br />
          WhatsApp thanks: {"{{1}}=customer_name"}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-200 hover:bg-zinc-700">Anulează</button>
          <button
            data-testid="customer-reviews-save-config"
            onClick={() => onSave(local)}
            disabled={saving}
            className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-400 disabled:opacity-50"
          >
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4" />
      <span className="text-sm text-zinc-200">{label}</span>
    </label>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none focus:border-amber-500/60"
      />
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs text-zinc-400 mb-1">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        min="1"
        className="w-full rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1.5 text-sm text-zinc-100 outline-none focus:border-amber-500/60"
      />
    </div>
  );
}

